/**
 * Commands — the only way to mutate GameState.
 *
 * Per MULTIPLAYER §2 every mutation is expressed as a typed Command that is
 * queued, serialised into the tick it applies to, and dispatched by a pure
 * reducer. This keeps the simulation deterministic and replayable and will
 * eventually allow commands from multiple peers to be merged by tick number.
 *
 * v1 command set is intentionally small — additional commands (research,
 * build-ship, etc.) are added as features land. MFix3.3 adds `colonise`.
 */

import type {
  Empire,
  EmpireId,
  EntityId,
  GameState,
  PlanetId,
  ResourceKind,
  ShipInstance,
  ShipInstanceId,
  Structure,
  StructureKind,
  SystemId,
  WarpGateLinkId,
} from '../state/types';
import { allocEntityId, EMPIRE_NEUTRAL_ID } from '../state/types';
import {
  CLOAKING_TECH_ID,
} from './balance-detection';
import {
  buildCreditsForKind,
  buildMineralsForKind,
  buildTicksForLevel,
  isUniqueKind,
  maxHullForLevel,
  maxLevelForKindByRace,
  maxStructuresForSize,
} from './balance-structures';
import { buildTicksByClass } from './balance-ships';
import { canBuildClassAtPlanet } from './planet-economy';
import {
  hyperdriveCostMultiplier,
  hyperdriveRangeMultiplier,
  hyperdriveSpeedMultiplier,
} from './research-effects';
import { chargeUpTicksForChassis, travelTicksFor } from './hyperdrive-time';
import {
  CONSTRUCTOR_CHASSIS_BY_RACE,
  MINEFIELD_CHASSIS,
  MINEFIELDS_TECH,
  SPACE_STRUCTURE_KINDS,
  STATIONARY_SPECIAL_ABILITY,
  WARP_GATE_CHASSIS,
  spaceBuildCostForKind,
  spaceBuildTicksForKind,
  spaceSiteRequirementForKind,
  warpGateConnectionBuildTicks,
  warpGateConnectionCostFuel,
  warpGateConnectionCostMinerals,
} from './balance-space-structures';
import {
  SQUADRON_CHASSIS_NAMES,
  isCarrierTemplate,
} from './balance-carrier-squadrons';
import {
  launchAllDocked,
  recallAllOwnSquadrons,
} from './advance-carrier-squadrons';
import {
  COLONISER_SPECIAL_ABILITY,
  colonisationRangeFor,
} from './balance-colonisation';
import {
  ADVANCED_TERRAFORMING_TECH,
  TERRAFORMING_TECH,
} from './balance-terraforming';
import { BIOME_XLSX_LABEL } from './balance-habitability';
import { despawnShip } from './advance-combat';
import { flipPlanet } from './advance-bombardment';
import { areTeammates } from './teams';

/**
 * Mk 1 hyperdrive efficiency baseline — ×1.0. Kept as a named constant so
 * tests and existing callers can still reference "the baseline".
 *
 * **Live fuel-cost multipliers are per-empire** and should be read via
 * `hyperdriveCostMultiplier(state, empire)` from research-effects.ts —
 * that function falls back to this constant when the empire has no
 * Hyperdrive Efficiency tech beyond Mk 1.
 */
export const HYPERDRIVE_MK1_EFFICIENCY = 1.0;

/** Command kinds, as a discriminated union. */
export type Command =
  | { kind: 'noop' }
  | {
      kind: 'grantResource';
      empireId: EmpireId;
      resource: ResourceKind;
      amount: number;
    }
  | {
      kind: 'setPopulation';
      planetId: PlanetId;
      population: number;
    }
  | {
      kind: 'jumpShips';
      /** Ships to jump — all must belong to the same empire and share a system. */
      shipIds: readonly ShipInstanceId[];
      /**
       * v4M5.3b3a — destination system per `HYPERSPACE.md §4` (continuous
       * Jump Area; no fixed JPs). The applier validates reachability via
       * `state.jumpGraph` and stamps `ship.jumpTargetSystemId`.
       */
      targetSystemId: SystemId;
      /**
       * v4M5.3b3a — arrival angle on the destination system's Jump Area
       * ring per §4.2. Radians (0 = +x, π/2 = +y); `null` defaults to
       * "from source" — the lander computes the angle pointing back at
       * the source system at arrival time.
       */
      arrivalAngle: number | null;
    }
  | {
      kind: 'moveShips';
      /**
       * Ships to order — all must be in the same system, belong to the same
       * empire, and not be mid-jump. Silently rejected if any check fails
       * (whole-batch atomic).
       */
      shipIds: readonly ShipInstanceId[];
      /** Target position in the ships' current system, local 0..1 coords. */
      targetX: number;
      targetY: number;
    }
  | {
      kind: 'moveAndJumpShips';
      /**
       * Ships to order — all must share a system, belong to the same
       * empire, and not be mid-jump. Whole-batch atomic: any failing
       * check silently rejects the entire command.
       *
       * On success each ship gets a sublight moveOrder aimed at the
       * source system's r=0.48 perimeter point facing the destination
       * (with formation offsets so they don't stack), plus
       * `pendingJumpSystemId = targetSystemId` (+ optional
       * `pendingJumpArrivalAngle`). `tryStartPendingJump` fires the
       * jump on sublight arrival; fuel is checked at fire time so
       * queued jumps don't block on current reserves.
       */
      shipIds: readonly ShipInstanceId[];
      /**
       * v4M5.3b3a — destination system per `HYPERSPACE.md §4` (continuous
       * Jump Area; no fixed JPs). Same reachability rules as `jumpShips`.
       */
      targetSystemId: SystemId;
      /**
       * v4M5.3b3a — arrival angle on the destination system's Jump Area
       * ring per §4.2. Radians; null defaults to "from source".
       */
      arrivalAngle: number | null;
    }
  | {
      kind: 'toggleCloak';
      /**
       * Ships to toggle — applier filters to those that belong to `empireId`,
       * have `canCloak: true` on their template, and whose empire has the
       * base Cloaking tech researched. Non-qualifying ships are silently
       * skipped. Toggle semantics are group-normalised: if ANY eligible
       * ship is currently cloaked, the command decloaks all eligible ships;
       * otherwise it cloaks all eligible ships that have enough energy for
       * at least one tick of drain.
       */
      shipIds: readonly ShipInstanceId[];
      /** Issuing empire. Ships owned by anyone else in the batch are ignored. */
      empireId: EmpireId;
    }
  | {
      /**
       * Start a new planet-side structure (STRUCTURES §2). Silently rejected
       * if any check fails: empire/planet missing, empire doesn't own planet,
       * slot cap reached, unique kind already present, insufficient resources.
       * On success the structure is created at level 0 with an initial build
       * countdown from `buildTicksForLevel(structuresData, kind, 1)`; cost is deducted once.
       * advance-structures.ts promotes it to level 1 when the countdown ends.
       */
      kind: 'buildStructure';
      empireId: EmpireId;
      planetId: PlanetId;
      structureKind: StructureKind;
    }
  | {
      /**
       * Upgrade an existing structure by one level (STRUCTURES §2 / §4).
       * Requires: structure exists, planet owned by `empireId`, structure is
       * at level 1..5 (not under initial construction, not already at cap),
       * not already mid-upgrade (buildTicksRemaining === 0), and empire has
       * resources for the flat per-level cost. Structure keeps its current
       * level during the upgrade (effects stay live); level promotes when
       * the countdown hits 0.
       */
      kind: 'upgradeStructure';
      empireId: EmpireId;
      structureId: EntityId;
    }
  | {
      /**
       * Set (or clear) an empire's active research target (M12). Pass
       * `techName: null` to clear — rpAccumulated is discarded. Silently
       * rejected when: empire doesn't exist, tech doesn't exist, tech is
       * already in researchedTechs, race-gated away, or prereqs not met.
       * Re-setting to the same tech the empire is already researching is
       * a no-op — progress is preserved. Setting a different tech resets
       * rpAccumulated to 0 per the M12 design lock.
       */
      kind: 'setResearchTarget';
      empireId: EmpireId;
      techName: string | null;
    }
  | {
      /**
       * Start a space-structure build on a Constructor-class ship (M14).
       * The Constructor travels to `(sx, sy)` at sublight (applier sets
       * moveOrder). advance-construction ticks the countdown only once
       * the Constructor has arrived (moveOrder === null). On completion
       * the structure spawns at `(sx, sy)`.
       *
       * Silently rejected when: empire or ship missing, ship not owned
       * by empire, ship is not the empire's Constructor chassis, ship is
       * mid-jump, already constructing, cloaked, or EMP-disabled, the
       * target kind isn't in SPACE_STRUCTURE_KINDS, the empire can't
       * afford it, or the kind requires an environmental site
       * (asteroid-field / gas-giant) that doesn't contain `(sx, sy)`.
       *
       * Costs (credits + minerals) are deducted up-front. Partial work
       * is lost on destruction / player cancel per STRUCTURES §3.
       */
      kind: 'buildSpaceStructure';
      empireId: EmpireId;
      constructorShipId: ShipInstanceId;
      structureKind: string;
      /** Build-site target in the Constructor's current system (local 0..1 coords). */
      sx: number;
      sy: number;
    }
  | {
      /**
       * Queue a ship build on a planet (M10). Silently rejected on any
       * failure: empire/planet missing, empire doesn't own planet, no
       * ShipTemplate for `empire.raceId:chassis`, no Shipyard at the
       * required class level, or insufficient credits/minerals/pop. On
       * success the order is pushed onto `planet.shipBuildQueue` with the
       * class-derived build time and the costs are deducted up front
       * (credits + minerals from the empire, popBuildCost from planet.currentPopulation).
       *
       * advance-shipyard.ts ticks the head of the queue each tick and
       * spawns the ship at a ring around the planet on completion.
       */
      kind: 'buildShip';
      empireId: EmpireId;
      planetId: PlanetId;
      chassis: string;
    }
  | {
      /**
       * Order one or more ships to bombard a planet (v2M5, PLANETS §2.7).
       * Silently rejected on any failure: empire/planet missing, planet
       * owned by the issuing empire (can't bombard your own world), or
       * ships not in the same system as the planet. Each successfully
       * ordered ship gets `bombardOrder = { targetPlanetId }` set and its
       * `constructionOrder` cleared (the two are mutually exclusive). The
       * `moveOrder` is left intact — the player can move ships into
       * bombardment range without overwriting the order; bombardment
       * fires whenever the ship is in range. New move/jump/cloak/build
       * commands clear bombardOrder per the existing exclusion pattern.
       */
      kind: 'bombardPlanet';
      empireId: EmpireId;
      shipIds: readonly ShipInstanceId[];
      targetPlanetId: PlanetId;
    }
  | {
      /**
       * MFix3.4 — sail a Coloniser to an uninhabited planet and auto-colonise
       * on arrival (RMB-on-uninhabited-planet UX). Mirrors `moveAndJumpShips`
       * for the colonisation pipeline.
       *
       * Validation chain (silent no-op):
       *   1. Empire / ship / planet exist; ship owned by `empireId`.
       *   2. Ship is not mid-jump (`systemId !== null`) and shares a system
       *      with the target planet.
       *   3. Ship's template carries the `'Colonise planet'` specialAbility.
       *
       * On success the ship gets `moveOrder = (planet.systemX, planet.systemY)`
       * plus `pendingColoniseTargetId = targetPlanetId`. `advancePendingColonisations`
       * (in `advance.ts`) invokes `applyColonise` once the ship arrives — the
       * full validation chain (Settlement tech, range, uninhabited, etc.) runs
       * at landing time, NOT here, so a player can queue the order while still
       * researching the biome tech and the colonise commits the moment both
       * conditions are met. A pending colonise that fails validation at arrival
       * silently drops; the ship sits at the planet doing nothing.
       *
       * Clears `pendingJumpSystemId`, `constructionOrder`, and `bombardOrder`
       * — same exclusion pattern as the other "set a new order" appliers.
       */
      kind: 'moveAndColonise';
      empireId: EmpireId;
      shipId: ShipInstanceId;
      targetPlanetId: PlanetId;
    }
  | {
      /**
       * MFix3.3 — colonise an uninhabited planet using a Coloniser-class
       * ship (PLANETS §2.2 / §2.7).
       *
       * Validation chain (silent no-op on failure):
       *   1. Empire / ship / planet exist; ship is owned by `empireId`.
       *   2. Ship's template carries the `'Colonise planet'` specialAbility.
       *   3. Ship is not mid-jump (`systemId !== null`) and shares a system
       *      with the target planet.
       *   4. Planet is uninhabited: `ownerEmpireId === null` AND
       *      `currentPopulation === 0` (depopulated planets are re-colonisable
       *      per the 2026-04-18 lock).
       *   5. Ship is within `colonisationRangeFor(planet.size)` of the
       *      planet's centre — derived from `planetSimRadiusForSize` plus
       *      a small approach margin.
       *   6. Empire has the matching `Settlement: <Biome>` tech researched.
       *      Gaia is universally colonisable per §2.2 — Settlement: Gaia is
       *      not a tech and is not required.
       *
       * On success:
       *   - Planet ownership transfers to `empireId`; loyalty resets to 100.
       *   - Population seeded from the ship template's `popBuildCost`
       *     (matches the design lock — 25 for all v1 colonisers).
       *   - Ship is despawned via the shared `despawnShip` path.
       *   - Planet id appended to the empire's `planetIds`.
       */
      kind: 'colonise';
      empireId: EmpireId;
      shipId: ShipInstanceId;
      targetPlanetId: PlanetId;
    }
  | {
      /**
       * MFix4.3 — start a terraform on a planet. Two paths:
       *   - **Coloniser path** (`coloniserShipId !== null`) — Terraforming
       *     L4 tech. Targets an uninhabited non-Gaia planet. Coloniser
       *     must be in colonisation range; ship is despawned via shared
       *     `despawnShip` on success (matches the colonise pattern).
       *   - **Planet-only path** (`coloniserShipId === null`) — Advanced
       *     Terraforming L6 tech. Targets an empire-owned non-Gaia planet.
       *     No ship required.
       *
       * Validation chain (silent no-op on failure):
       *   1. Empire / planet exist; planet biome is not already 'gaia';
       *      planet is not already mid-terraform.
       *   2. Coloniser path: ship exists, owned by `empireId`, has the
       *      `'Colonise planet'` specialAbility, in same system as planet,
       *      within `colonisationRangeFor(planet.size)`. Empire has
       *      `'Terraforming'` researched. Planet uninhabited
       *      (`ownerEmpireId === null` AND `currentPopulation === 0`).
       *   3. Planet-only path: Empire has `'Advanced Terraforming'`
       *      researched. Planet owned by `empireId`.
       *   4. Empire has resources: `TERRAFORMING_MINERALS_COST` minerals
       *      AND `TERRAFORMING_FUEL_COST` fuel.
       *
       * On success:
       *   - Deduct resources.
       *   - Set `planet.terraforming = { targetBiome: 'gaia',
       *     ticksRemaining: TERRAFORMING_DURATION_TICKS, fuelCost,
       *     mineralsCost }`.
       *   - Coloniser path only: despawn ship via `despawnShip`.
       *
       * `advance-terraforming.ts` (already wired since M2 scaffolding)
       * ticks down `ticksRemaining` and flips `planet.biome = 'gaia'`
       * on completion.
       */
      kind: 'startTerraform';
      empireId: EmpireId;
      targetPlanetId: PlanetId;
      coloniserShipId: ShipInstanceId | null;
    }
  | {
      /**
       * v3M12.4 — manual surrender. Flips `state.gameOutcome` to
       * `'defeat'` directly, bypassing the
       * `advance-game-end.ts` planet-tally path. Player's ships +
       * planets stay frozen on the final frame so the player can
       * pan around and inspect the state at the moment of giving up.
       *
       * No-op if the game is already over (idempotent re-dispatch
       * during a stuck UI doesn't double-flip).
       */
      kind: 'concede';
    }
  | {
      /**
       * v3M18 — author a Warp Gate connection between two of an
       * empire's gates. Creates a `'building'` link with progressTicks
       * 0; `advanceWarpGateLinks` ticks it to `'live'` over
       * `connectionBuildTicks` (locked at 600) ticks. Cost
       * (`connectionCostMinerals` + `connectionCostFuel`) is debited
       * at issue.
       *
       * Validation chain (silent no-op on any failure):
       *   1. Empire / both gates exist.
       *   2. Both gates are owned by `empireId` and have chassis
       *      `'Warp Gate'`.
       *   3. Both gates are operational (have a `systemId` — i.e. not
       *      mid-jump or in a synthetic limbo state).
       *   4. The two gates are in different systems (no self-links).
       *   5. Neither gate is already an endpoint of any link in
       *      `state.warpGateLinks` (one connection per gate).
       *   6. Empire has the resources for the cost.
       */
      kind: 'connectWarpGate';
      empireId: EmpireId;
      gateAId: ShipInstanceId;
      gateBId: ShipInstanceId;
    }
  | {
      /**
       * v3M18 — drop an existing Warp Gate connection. Instant + free
       * regardless of `'building'` or `'live'` status. Surviving gates
       * are immediately eligible to be the endpoint of a new link.
       *
       * Validation chain (silent no-op on failure):
       *   1. Empire / link exist.
       *   2. Link is owned by `empireId`.
       */
      kind: 'disconnectWarpGate';
      empireId: EmpireId;
      linkId: WarpGateLinkId;
    }
  | {
      /**
       * v3M18 — initiate a hyperspace jump for one or more ships via a
       * live Warp Gate connection. Ships emerge at the partner gate's
       * (sx, sy) in the partner system. Travel duration is scaled by
       * galaxy-distance between the two gate-systems, so connections
       * to nearby systems travel faster than connections to far ones.
       *
       * Validation chain (whole-batch atomic):
       *   1. Link exists, is `'live'`, and is owned by the ships'
       *      shared empire.
       *   2. Both endpoint gates still exist (defensive — sever-on-
       *      destroy normally drops the link before this matters).
       *   3. All ships share one empire and that empire owns the link.
       *   4. All ships sit in the same system (one of the two gate
       *      systems); ships mid-jump or in the wrong system reject
       *      the whole batch.
       *   5. Empire has Σ (hyperdriveFuelUsePerUnit × pathDistance) fuel for the batch.
       *
       * On success: deduct fuel; for each ship set jumpOrigin/target
       * (system + GateId), jumpTicksRemaining = max(50,
       * round(galaxyDistance × tmpl.jumpDurationTicks)), clear
       * sublight + colonise + bombard orders, clear systemId.
       */
      kind: 'jumpShipsViaWarpGate';
      shipIds: readonly ShipInstanceId[];
      linkId: WarpGateLinkId;
    }
  /**
   * v3M17.2 — Manual carrier squadron commands. Override the auto-launch
   * / auto-recall pass on a single carrier. Detail-panel "Launch all
   * squadrons" / "Recall all squadrons" buttons surface these (lands in
   * v3M17.4). Validation: empire owns the carrier, carrier is a real
   * carrier-template chassis, carrier is in-system / not mid-jump /
   * not EMP-disabled / not destroyed / not cloaked.
   */
  | {
      kind: 'launchCarrierSquadrons';
      empireId: EmpireId;
      carrierShipId: ShipInstanceId;
    }
  | {
      kind: 'recallCarrierSquadrons';
      empireId: EmpireId;
      carrierShipId: ShipInstanceId;
    }
  /**
   * v3M17.2 — Per-carrier behaviour setters. Set the Attack / Defense
   * Behaviour for a specific carrier. Validation: empire owns the
   * carrier, carrier is a real carrier-template chassis. Setting on a
   * non-carrier silently no-ops; setting on a destroyed carrier is
   * allowed (so the player can configure a queued build's carrier on
   * arrival without timing dance).
   */
  | {
      kind: 'setCarrierAttackBehaviour';
      empireId: EmpireId;
      carrierShipId: ShipInstanceId;
      behaviour: 'none' | 'visual-range' | 'scan-range';
    }
  | {
      kind: 'setCarrierDefenseBehaviour';
      empireId: EmpireId;
      carrierShipId: ShipInstanceId;
      behaviour: 'return-at-low' | 'no-surrender';
    }
  // -------------------------------------------------------------------------
  // v3M22 — Player ship commands
  // -------------------------------------------------------------------------
  | {
      /**
       * v3M22 — set the ship into Patrol mode. Loops the ship's existing
       * `waypoints` chain forever via `advance-ship-commands`. No-op if
       * the ship has zero waypoints (the player must place at least one
       * waypoint first via setShipWaypoints / appendShipWaypoint).
       */
      kind: 'setShipPatrol';
      empireId: EmpireId;
      shipId: ShipInstanceId;
    }
  | {
      /**
       * v3M22 — set the ship into Guard mode targeting a planet (orbit
       * the planet at planetSimRadius + 0.04) or a friendly ship (follow
       * at ~0.05 leash). Validates target existence + same-system at
       * issue time; rejects silently if the target isn't reachable.
       */
      kind: 'setShipGuard';
      empireId: EmpireId;
      shipId: ShipInstanceId;
      targetKind: 'planet' | 'ship';
      targetId: number;
    }
  | {
      /**
       * v3M22 — set the ship into Explore mode. Auto-jumps to the
       * least-scouted reachable system every AI-tick interval until all
       * reachable systems are scouted, then auto-clears to 'idle'.
       */
      kind: 'setShipExplore';
      empireId: EmpireId;
      shipId: ShipInstanceId;
    }
  | {
      /**
       * v3M22 — replace the ship's waypoint queue with `waypoints`. Used
       * by the plain-RMB-on-destination UX. Setting an empty array is
       * legal (clears the queue without changing commandKind).
       */
      kind: 'setShipWaypoints';
      empireId: EmpireId;
      shipId: ShipInstanceId;
      waypoints: ReadonlyArray<{ targetX: number; targetY: number }>;
    }
  | {
      /**
       * v3M22 — append one waypoint to the ship's queue. Used by the
       * Shift+RMB-on-destination UX. Doesn't change commandKind.
       */
      kind: 'appendShipWaypoint';
      empireId: EmpireId;
      shipId: ShipInstanceId;
      targetX: number;
      targetY: number;
    }
  | {
      /**
       * v3M22 — clear all v3M22 command state on the ship: commandKind
       * back to 'idle', waypoints to [], guardTargetKind / guardTargetId
       * to null. Used by the player's Stop button + Esc + the cancel-on-
       * jump path. Does not touch the ship's existing single-shot orders
       * (moveOrder, bombardOrder, etc.).
       */
      kind: 'clearShipCommand';
      empireId: EmpireId;
      shipId: ShipInstanceId;
    }
  | {
      /**
       * v3M21.2 — set or clear the empire's diplomatic focus target
       * (`DIPLOMACY.md §4`). While focus is non-null, the empire's
       * per-tick Diplomatic income routes directly into the focused
       * planet's per-empire Influence pool (`Planet.influence`)
       * instead of accumulating in `empire.resources.diplomatic`.
       *
       * Validation:
       *   - empire exists.
       *   - planetId === null clears focus unconditionally.
       *   - planetId non-null: planet exists AND is currently a neutral
       *     (`ownerEmpireId === EMPIRE_NEUTRAL_ID`). Rejected silently
       *     for owned / uninhabited / synthetic-neutral-itself targets.
       *
       * Previously-accumulated Influence on the prior focus target
       * stays put — Influence is a one-way ratchet (`DIPLOMACY.md §5`).
       */
      kind: 'setDiplomaticFocus';
      empireId: EmpireId;
      planetId: PlanetId | null;
    }
  | {
      /**
       * v3M21.2 — one-shot transfer from `empire.resources.diplomatic`
       * into the empire's current focus target's per-empire Influence
       * pool (`Planet.influence[empireId]`). Used to dump treasury
       * Diplomatic into a focused neutral on demand, on top of the
       * per-tick income routing that runs in `advance.ts`.
       *
       * Validation:
       *   - empire exists.
       *   - `empire.diplomaticFocus` is non-null.
       *   - focus target planet exists AND is currently a neutral
       *     (defensive recheck — the focus could have been pointed at
       *     a planet that's since been annexed by another empire and
       *     the per-tick auto-clear hasn't run yet).
       *   - `amount > 0`.
       *   - `empire.resources.diplomatic >= amount`.
       *
       * Silent no-op on rejection. No partial transfer — either the
       * full amount moves or nothing does.
       */
      kind: 'transferDiplomaticToFocus';
      empireId: EmpireId;
      amount: number;
    }
  | {
      /**
       * v3M21.3 — invoke the Annex outcome on a neutral planet
       * (`DIPLOMACY.md §6.1`). Planet's `ownerEmpireId` flips to the
       * invoking empire via the same `flipPlanet` helper used by the
       * bombardment-driven Loyalty=0 flip — Loyalty resets to 100,
       * structures transfer intact (race-specific scrapped), pop
       * transfers fully, all per-empire Influence pools on this
       * planet collapse, Federation / Trade / Vassal arrangements
       * clear, every empire's diplomaticFocus that pointed here is
       * cleared.
       *
       * Validation:
       *   - empire exists.
       *   - planet exists AND is currently a neutral
       *     (`ownerEmpireId === EMPIRE_NEUTRAL_ID`).
       *   - empire's Influence pool on this planet is at or above
       *     `state.balanceData.diplomacy.thresholds.annex`.
       *
       * The threshold is a *gate*, not a *cost* — Annex doesn't drain
       * the empire's accumulated Influence (the planet's Influence
       * Map is cleared as a side effect of the flip, but no new
       * resource debit lands on the empire). Same model for the
       * other outcomes.
       */
      kind: 'invokeAnnex';
      empireId: EmpireId;
      planetId: PlanetId;
    }
  | {
      /**
       * v3M21.3 — invoke the Trade outcome on a neutral planet
       * (`DIPLOMACY.md §6.3`). Empire is pushed onto the planet's
       * `tradingWith[]`; per-tick Trade income (Credits + Minerals)
       * starts flowing to the empire's resources via the
       * `advance-trade.ts` pass.
       *
       * Validation:
       *   - empire exists.
       *   - empire is not the synthetic neutral.
       *   - planet exists AND is currently a neutral.
       *   - empire's Influence pool ≥ `tradeThreshold` (default 200).
       *   - empire is not already in `tradingWith` for this planet
       *     (idempotent — re-invoking is a no-op rather than double-
       *     pushing).
       *
       * Trade is non-exclusive: multiple empires can trade with the
       * same neutral simultaneously. Trade does NOT drain the
       * empire's Influence pool; the threshold gates the action.
       * Federation lock (v3M21.4) caps non-federated empires' pools
       * at `federationThreshold` (400), which is above
       * `tradeThreshold` (200), so Federation does not block Trade.
       */
      kind: 'invokeTrade';
      empireId: EmpireId;
      planetId: PlanetId;
    }
  | {
      /**
       * v3M21.3 — empire voluntarily ends a Trade arrangement with a
       * neutral planet. Removes the empire from `Planet.tradingWith[]`.
       *
       * Validation:
       *   - empire exists.
       *   - planet exists.
       *   - empire is currently in `tradingWith[]`.
       *
       * Other empires' Trade arrangements with this planet are
       * unaffected.
       */
      kind: 'clearTrade';
      empireId: EmpireId;
      planetId: PlanetId;
    }
  | {
      /**
       * v3M21.4 — invoke the Federation outcome on a neutral planet
       * (`DIPLOMACY.md §6.2`). Sets `Planet.federatedWith = empireId`
       * and adds the planet's system to the empire's
       * `scoutedSystemIds` (vision share — one-way reveal that
       * persists like any other scouted system).
       *
       * Validation:
       *   - empire exists.
       *   - empire is not the synthetic neutral.
       *   - planet exists AND is currently a neutral.
       *   - planet is not already federated to a rival empire (the
       *     "chicken-and-egg" lock: a rival empire's pool would be
       *     capped at federationThreshold by v3M21.4.2's routing
       *     guard, so reaching the threshold to invoke is already
       *     impossible. The validation here is defensive — handles
       *     the edge where the lock hasn't yet ticked through, and
       *     rejects identical-empire re-invokes idempotently).
       *   - empire's Influence pool ≥ federationThreshold (default 400).
       *
       * Federation prevents rivals from invoking Federation, Vassal,
       * or Annex (the lock pins their pools at federationThreshold,
       * which is below all three of those thresholds — Annex 800,
       * Vassal 600, Federation 400). Trade (200) is below the lock
       * and remains available to non-federated empires.
       */
      kind: 'invokeFederation';
      empireId: EmpireId;
      planetId: PlanetId;
    }
  | {
      /**
       * v3M21.4 — federated empire voluntarily ends the federation.
       * Sets `Planet.federatedWith = null`. The empire keeps the
       * planet's system in `scoutedSystemIds` — one-way reveal,
       * matching the existing FoW model.
       *
       * Validation:
       *   - empire exists.
       *   - planet exists.
       *   - planet is currently federated to THIS empire (rejects
       *     attempts by rivals to break someone else's federation).
       */
      kind: 'clearFederation';
      empireId: EmpireId;
      planetId: PlanetId;
    }
  | {
      /**
       * v3M21.4 — invoke the Vassal outcome on a neutral planet
       * (`DIPLOMACY.md §6.4`). Sets `Planet.vassalOf = empireId`. If
       * the planet was already vassal of a different empire, that
       * relationship breaks and the new suzerain takes over (vassal
       * flip). Per-tick `vassalTributeFraction` of the planet's
       * resource production routes to the suzerain via
       * `advance.ts:3c` (lands in v3M21.4.4).
       *
       * Validation:
       *   - empire exists.
       *   - empire is not the synthetic neutral.
       *   - planet exists AND is currently a neutral.
       *   - planet is NOT currently federated (Federation supersedes
       *     Vassal — even the federated empire cannot invoke Vassal
       *     on their own federated planet without clearing Federation
       *     first; matches DIPLOMACY.md §6.4 "possible only if
       *     planet is not federated").
       *   - empire's Influence pool ≥ `vassalThreshold` (default 600).
       *
       * The Federation lock (v3M21.4.2) caps non-federated empires'
       * pools at federationThreshold (400) < vassalThreshold (600),
       * so an empire that wasn't federated would never reach the
       * threshold under an active federation. The federation guard
       * here covers the residual edge where pool was accumulated
       * before federation started.
       */
      kind: 'invokeVassal';
      empireId: EmpireId;
      planetId: PlanetId;
    }
  | {
      /**
       * v3M21.4 — current suzerain voluntarily ends vassalage. Sets
       * `Planet.vassalOf = null`.
       *
       * Validation:
       *   - empire exists.
       *   - planet exists.
       *   - planet is currently vassal-of THIS empire (rivals cannot
       *     break someone else's vassal relationship).
       */
      kind: 'clearVassal';
      empireId: EmpireId;
      planetId: PlanetId;
    };

/**
 * v4M5.2b — Distance-scaled fuel cost across the given ships, scaled
 * by Mk efficiency. Per `docs/HYPERSPACE.md §3`:
 *
 *   fuelPerJump = Σ over fleet ships of
 *                 (ship.hyperdriveFuelUsePerUnit × pathDistance × hyperdriveEfficiencyMul)
 *
 * `pathDistance` is the galaxy-frame Euclidean distance between source
 * and destination systems. Pure helper exposed so UI / AI can show
 * cost before issuing the command. Missing ships or templates are
 * silently skipped — the cost is only a lower bound if any ship is
 * unresolved, but the same filter runs in applyJumpShips so the actual
 * deduction matches.
 */
export function fuelCostForJump(
  state: GameState,
  shipIds: readonly ShipInstanceId[],
  pathDistance: number,
): number {
  let sum = 0;
  let empire: Empire | undefined;
  for (const shipId of shipIds) {
    const ship = state.ships.get(shipId);
    if (!ship) continue;
    const e = state.empires.get(ship.ownerEmpireId);
    if (!e) continue;
    if (!empire) empire = e;
    const tmpl = state.shipTemplates.get(`${e.raceId}:${ship.chassis}`);
    if (!tmpl) continue;
    sum += tmpl.hyperdriveFuelUsePerUnit;
  }
  // Ships share an empire per applyJumpShips' validator; any valid one
  // gives us the correct multiplier. Fall back to 1.0 if no empire
  // resolved (empty batch).
  const multiplier = empire ? hyperdriveCostMultiplier(state, empire) : 1.0;
  return sum * pathDistance * multiplier;
}

/**
 * Apply a single command. Pure w.r.t. to its own scope: it mutates `state` but
 * the mutation is fully determined by `state` and `cmd`.
 */
export function applyCommand(state: GameState, cmd: Command): void {
  switch (cmd.kind) {
    case 'noop':
      return;
    case 'grantResource': {
      const empire = state.empires.get(cmd.empireId);
      if (!empire) return;
      empire.resources[cmd.resource] += cmd.amount;
      return;
    }
    case 'setPopulation': {
      const planet = state.planets.get(cmd.planetId);
      if (!planet) return;
      planet.currentPopulation = Math.max(0, Math.min(planet.populationCap, cmd.population));
      return;
    }
    case 'jumpShips': {
      // v4M5.3b3a — appliers consume targetSystemId + arrivalAngle
      // natively per `HYPERSPACE.md §4`. Continuous Jump Area; no JP
      // resolution.
      applyJumpShips(state, cmd.shipIds, cmd.targetSystemId, cmd.arrivalAngle);
      return;
    }
    case 'moveShips': {
      applyMoveShips(state, cmd.shipIds, cmd.targetX, cmd.targetY);
      return;
    }
    case 'moveAndJumpShips': {
      applyMoveAndJumpShips(
        state,
        cmd.shipIds,
        cmd.targetSystemId,
        cmd.arrivalAngle,
      );
      return;
    }
    case 'toggleCloak': {
      applyToggleCloak(state, cmd.shipIds, cmd.empireId);
      return;
    }
    case 'buildStructure': {
      applyBuildStructure(state, cmd.empireId, cmd.planetId, cmd.structureKind);
      return;
    }
    case 'upgradeStructure': {
      applyUpgradeStructure(state, cmd.empireId, cmd.structureId);
      return;
    }
    case 'buildShip': {
      applyBuildShip(state, cmd.empireId, cmd.planetId, cmd.chassis);
      return;
    }
    case 'buildSpaceStructure': {
      applyBuildSpaceStructure(
        state,
        cmd.empireId,
        cmd.constructorShipId,
        cmd.structureKind,
        cmd.sx,
        cmd.sy,
      );
      return;
    }
    case 'setResearchTarget': {
      applySetResearchTarget(state, cmd.empireId, cmd.techName);
      return;
    }
    case 'concede': {
      applyConcede(state);
      return;
    }
    case 'bombardPlanet': {
      applyBombardPlanet(state, cmd.empireId, cmd.shipIds, cmd.targetPlanetId);
      return;
    }
    case 'colonise': {
      applyColonise(state, cmd.empireId, cmd.shipId, cmd.targetPlanetId);
      return;
    }
    case 'moveAndColonise': {
      applyMoveAndColonise(state, cmd.empireId, cmd.shipId, cmd.targetPlanetId);
      return;
    }
    case 'startTerraform': {
      applyStartTerraform(
        state,
        cmd.empireId,
        cmd.targetPlanetId,
        cmd.coloniserShipId,
      );
      return;
    }
    case 'connectWarpGate': {
      applyConnectWarpGate(state, cmd.empireId, cmd.gateAId, cmd.gateBId);
      return;
    }
    case 'disconnectWarpGate': {
      applyDisconnectWarpGate(state, cmd.empireId, cmd.linkId);
      return;
    }
    case 'jumpShipsViaWarpGate': {
      applyJumpShipsViaWarpGate(state, cmd.shipIds, cmd.linkId);
      return;
    }
    case 'launchCarrierSquadrons': {
      applyLaunchCarrierSquadrons(state, cmd.empireId, cmd.carrierShipId);
      return;
    }
    case 'recallCarrierSquadrons': {
      applyRecallCarrierSquadrons(state, cmd.empireId, cmd.carrierShipId);
      return;
    }
    case 'setCarrierAttackBehaviour': {
      applySetCarrierAttackBehaviour(
        state,
        cmd.empireId,
        cmd.carrierShipId,
        cmd.behaviour,
      );
      return;
    }
    case 'setCarrierDefenseBehaviour': {
      applySetCarrierDefenseBehaviour(
        state,
        cmd.empireId,
        cmd.carrierShipId,
        cmd.behaviour,
      );
      return;
    }
    case 'setShipPatrol': {
      applySetShipPatrol(state, cmd.empireId, cmd.shipId);
      return;
    }
    case 'setShipGuard': {
      applySetShipGuard(state, cmd.empireId, cmd.shipId, cmd.targetKind, cmd.targetId);
      return;
    }
    case 'setShipExplore': {
      applySetShipExplore(state, cmd.empireId, cmd.shipId);
      return;
    }
    case 'setShipWaypoints': {
      applySetShipWaypoints(state, cmd.empireId, cmd.shipId, cmd.waypoints);
      return;
    }
    case 'appendShipWaypoint': {
      applyAppendShipWaypoint(state, cmd.empireId, cmd.shipId, cmd.targetX, cmd.targetY);
      return;
    }
    case 'clearShipCommand': {
      applyClearShipCommand(state, cmd.empireId, cmd.shipId);
      return;
    }
    case 'setDiplomaticFocus': {
      applySetDiplomaticFocus(state, cmd.empireId, cmd.planetId);
      return;
    }
    case 'transferDiplomaticToFocus': {
      applyTransferDiplomaticToFocus(state, cmd.empireId, cmd.amount);
      return;
    }
    case 'invokeAnnex': {
      applyInvokeAnnex(state, cmd.empireId, cmd.planetId);
      return;
    }
    case 'invokeTrade': {
      applyInvokeTrade(state, cmd.empireId, cmd.planetId);
      return;
    }
    case 'clearTrade': {
      applyClearTrade(state, cmd.empireId, cmd.planetId);
      return;
    }
    case 'invokeFederation': {
      applyInvokeFederation(state, cmd.empireId, cmd.planetId);
      return;
    }
    case 'clearFederation': {
      applyClearFederation(state, cmd.empireId, cmd.planetId);
      return;
    }
    case 'invokeVassal': {
      applyInvokeVassal(state, cmd.empireId, cmd.planetId);
      return;
    }
    case 'clearVassal': {
      applyClearVassal(state, cmd.empireId, cmd.planetId);
      return;
    }
    default: {
      // Exhaustiveness check — if a new kind is added, TS will flag this.
      const _exhaustive: never = cmd;
      void _exhaustive;
    }
  }
}

// ---------------------------------------------------------------------------
// Colonise (MFix3.3)
// ---------------------------------------------------------------------------

/**
 * Validate and commit a colonisation. Pure mutation; silent no-op on any
 * failure per the MULTIPLAYER §2 command-reducer contract.
 *
 * Exported so tests can drive it directly without going through `applyCommand`.
 */
export function applyColonise(
  state: GameState,
  empireId: EmpireId,
  shipId: ShipInstanceId,
  targetPlanetId: PlanetId,
): void {
  // 1. Resolve all entities.
  const empire = state.empires.get(empireId);
  if (!empire) return;
  const ship = state.ships.get(shipId);
  if (!ship) return;
  if (ship.ownerEmpireId !== empireId) return;
  const planet = state.planets.get(targetPlanetId);
  if (!planet) return;
  const tmpl = state.shipTemplates.get(`${empire.raceId}:${ship.chassis}`);
  if (!tmpl) return;

  // 2. Coloniser specialAbility.
  if (!tmpl.specialAbilities.includes(COLONISER_SPECIAL_ABILITY)) return;

  // 3. Same-system / not mid-jump.
  if (ship.systemId === null) return;
  if (ship.systemId !== planet.systemId) return;

  // 4. Planet uninhabited (depopulated planets re-colonisable per the
  //    2026-04-18 PLANETS §2.7 revised lock).
  if (planet.ownerEmpireId !== null) {
    pushRejection(
      state,
      'colonise',
      empireId,
      `${planet.name} is already owned by another empire.`,
    );
    return;
  }
  if (planet.currentPopulation > 0) {
    pushRejection(
      state,
      'colonise',
      empireId,
      `${planet.name} is already populated.`,
    );
    return;
  }

  // 5. In colonisation range (planet sim radius + approach margin).
  const range = colonisationRangeFor(planet.size, state.balanceData);
  const dx = ship.sx - planet.systemX;
  const dy = ship.sy - planet.systemY;
  if (dx * dx + dy * dy > range * range) {
    pushRejection(
      state,
      'colonise',
      empireId,
      `Coloniser is too far from ${planet.name}.`,
    );
    return;
  }

  // 6. Settlement-tech gate. Gaia is universal per PLANETS §2.2 — no tech
  //    needed; all other biomes require the matching Settlement: <Biome>.
  if (planet.biome !== 'gaia') {
    const xlsxBiome = BIOME_XLSX_LABEL[planet.biome];
    const settlementTech = `Settlement: ${xlsxBiome}`;
    if (!empire.researchedTechs.has(settlementTech)) {
      pushRejection(
        state,
        'colonise',
        empireId,
        `Colonising ${planet.name} requires the ${settlementTech} tech.`,
      );
      return;
    }
  }

  // Commit.
  planet.ownerEmpireId = empireId;
  planet.currentPopulation = tmpl.popBuildCost;
  planet.loyalty = state.balanceData.colonisation.initialLoyalty;
  empire.planetIds.push(planet.id);
  despawnShip(state, shipId);
}

/**
 * Validate + apply a per-ship jump. Silent no-op on every failure mode so a
 * malformed/stale command from the input layer can't corrupt sim state.
 *
 * Validation chain:
 *   1. Jump point exists and is linked.
 *   2. At least one ship resolves; all resolved ships share one owner empire;
 *      every one of them currently sits in the JP's system and isn't already
 *      mid-jump.
 *   3. Owner empire has Σ (hyperdriveFuelUsePerUnit × pathDistance) fuel for the whole batch.
 * On success: deduct fuel once, and for each ship set jumpOrigin to current
 * system, jumpTarget to the linked system, jumpTicksRemaining to that ship's
 * template jumpDurationTicks (so a fast Scout lands before a slow Cruiser),
 * and clear systemId.
 *
/**
 * v3M2.1 — true if `empireId` already has a Warp Gate (operational OR
 * under construction) in `systemId`. Used by the build-command
 * uniqueness gate. "Under construction" is detected via the
 * Constructor's `constructionOrder.structureKind === 'Warp Gate'` —
 * the gate hasn't spawned yet but the slot is reserved.
 */
function empireOwnsWarpGateInSystem(
  state: GameState,
  empireId: EmpireId,
  systemId: number,
): boolean {
  for (const s of state.ships.values()) {
    if (s.ownerEmpireId !== empireId) continue;
    if (s.systemId !== systemId) continue;
    if (s.chassis === WARP_GATE_CHASSIS) return true;
    // A Constructor mid-build of a Warp Gate also counts so two
    // queued builds can't race to completion in the same system.
    if (
      s.constructionOrder !== null &&
      s.constructionOrder.structureKind === WARP_GATE_CHASSIS
    ) {
      return true;
    }
  }
  return false;
}

/**
 * v3M18 — find a `'live'` Warp Gate connection between two systems
 * for the given empire, or `null` if none exists. Used by:
 *   - The galaxy-view rendering pass (v3M18.3) to decide which red
 *     dotted lines to draw.
 *   - The connect-mode UI (v3M18.4) to show the player a live link
 *     they're about to disconnect.
 *   - The jumpShipsViaWarpGate command (v3M18.4) to validate the
 *     route and compute distance-scaled travel duration.
 *
 * `'building'` links are intentionally excluded — only live links
 * provide travel. Caller can scan `state.warpGateLinks` directly if
 * they need the building-or-live set.
 *
 * Symmetric in (sysA, sysB) — the link record stores its own gateA /
 * gateB ordering and the predicate matches either direction.
 */
export function findLiveWarpGateLink(
  state: GameState,
  sysA: SystemId,
  sysB: SystemId,
  empireId: EmpireId,
): import('../state/types').WarpGateLink | null {
  if (sysA === sysB) return null;
  for (const link of state.warpGateLinks.values()) {
    if (link.status !== 'live') continue;
    if (link.ownerEmpireId !== empireId) continue;
    if (
      (link.systemAId === sysA && link.systemBId === sysB) ||
      (link.systemAId === sysB && link.systemBId === sysA)
    ) {
      return link;
    }
  }
  return null;
}

/**
 * v3M18 — true if `gateId` resolves to a Warp Gate ShipInstance that
 * is the endpoint of a `'building'` or `'live'` link in
 * `state.warpGateLinks`. Used by the connect-command validator to
 * reject "this gate already has a connection" before debiting cost.
 */
function gateIsLinked(state: GameState, gateId: ShipInstanceId): boolean {
  for (const link of state.warpGateLinks.values()) {
    if (link.gateAId === gateId || link.gateBId === gateId) return true;
  }
  return false;
}

/**
 * MFix5.2 — true if `ship` is a stationary space structure (Weapons
 * Platform, Sensor Buoy, Repair Yard, Asteroid Mining Outpost, Gas
 * Giant Refinery). Discriminator is the synthetic template's
 * `STATIONARY_SPECIAL_ABILITY` flag, stamped in
 * `loader.ts:SPACE_STRUCTURE_SPECS`. These ships are Constructor-placed
 * at a fixed (sx, sy) and must reject every move/jump command,
 * otherwise a platform built near a jump point can warp out under a
 * player RMB-on-JP gesture (it sits at the JP, the arrival check
 * passes, the auto-jump fires).
 *
 * Earlier revision used `sublightSpeed === 0` as the discriminator
 * but several test fixtures leave that field at 0 as a placeholder,
 * triggering false positives. The specialAbility flag is intentional
 * and unambiguous.
 */
function isStationaryPlatform(state: GameState, ship: ShipInstance): boolean {
  const empire = state.empires.get(ship.ownerEmpireId);
  if (!empire) return false;
  const tmpl = state.shipTemplates.get(`${empire.raceId}:${ship.chassis}`);
  return (
    tmpl !== undefined &&
    tmpl.specialAbilities.includes(STATIONARY_SPECIAL_ABILITY)
  );
}

/**
 * "Atomic across the batch" — we validate every ship before mutating anything,
 * so a single ineligible ship or insufficient fuel cancels the whole order.
 */
function applyJumpShips(
  state: GameState,
  shipIds: readonly ShipInstanceId[],
  targetSystemId: SystemId,
  arrivalAngle: number | null,
): void {
  // v4M5.3b3a — continuous Jump Area: no JP lookup. Destination system
  // identity comes from the command; source system is resolved from
  // the ships during validation below.
  const dstSystem = state.systems.get(targetSystemId);
  if (!dstSystem) return;

  // Resolve ships and validate in one pass.
  interface Entry {
    ship: ShipInstance;
    // v4M5.2c — per-ship duration is now computed as
    // chargeUp + travelTicksFor(pathDistance, hyperdriveSpeed,
    // hyperdriveSpeedMultiplier). The Entry captures the inputs;
    // the per-ship duration is finalised after pathDistance lands.
    chargeUpTicks: number;
    hyperdriveSpeed: number;
    // v4M5.2b — per-ship fuel cost is now distance-scaled. The Entry
    // captures the per-unit rate; the batch-total cost multiplies by
    // pathDistance below.
    fuelUsePerUnit: number;
  }
  const entries: Entry[] = [];
  let empireId: EmpireId | null = null;
  let sourceSystemId: SystemId | null = null;
  for (const shipId of shipIds) {
    const ship = state.ships.get(shipId);
    if (!ship) return; // unknown ship → reject whole batch
    if (ship.systemId === null) return; // mid-jump → reject
    if (ship.jumpTargetSystemId !== null) return; // already mid-jump
    if (sourceSystemId === null) sourceSystemId = ship.systemId;
    else if (sourceSystemId !== ship.systemId) return; // ships split across systems
    if (empireId === null) empireId = ship.ownerEmpireId;
    else if (empireId !== ship.ownerEmpireId) return; // mixed owners
    const empire = state.empires.get(ship.ownerEmpireId);
    if (!empire) return;
    const tmpl = state.shipTemplates.get(`${empire.raceId}:${ship.chassis}`);
    if (!tmpl) return;
    // MFix5.2 — Stationary platforms (sublightSpeed === 0) are filtered
    // out instead of rejecting the whole batch, so a mixed selection
    // (Cruisers + Platform) sees the Cruisers jump and the Platform
    // stay put.
    if (tmpl.sublightSpeed <= 0) continue;
    // v4M5.2c — chargeUp + distance-scaled travel time supersedes the
    // legacy `tmpl.jumpDurationTicks` field. Per-ship duration computed
    // below once pathDistance is available; per-ship charge-up keyed
    // off chassis class per `HYPERSPACE.md §6`.
    entries.push({
      ship,
      chargeUpTicks: chargeUpTicksForChassis(ship.chassis),
      hyperdriveSpeed: tmpl.hyperdriveSpeed,
      fuelUsePerUnit: tmpl.hyperdriveFuelUsePerUnit,
    });
  }
  if (entries.length === 0 || empireId === null) return;

  const empire = state.empires.get(empireId);
  if (!empire) return;

  // v4M5.2a — range check at order-issue per `docs/HYPERSPACE.md §1.1`.
  // v4M5.2b — pathDistance also feeds the distance-scaled fuel formula
  // per §3. Both gates reuse the same computed distance.
  // Fleet effective range = min over ships of (chassis.hyperdriveRange ×
  // hyperdriveRangeMultiplier). The shortest-range ship constrains the
  // batch (same pattern as the charge-up duration where the slowest sets
  // the pace). If the source-to-destination galaxy-frame distance exceeds
  // the fleet's effective range, reject the whole order — the player
  // must split the fleet or research Hyperdrive Range to extend it.
  // v4M5.3b3a — sourceSystemId resolved during ship validation;
  // dstSystem already resolved at function entry. Continuous Jump Area
  // model — both systems are first-class state.systems entries.
  if (sourceSystemId === null) return; // unreachable (entries.length === 0 caught above)
  if (sourceSystemId === targetSystemId) return; // can't jump to your own system
  const srcSystem = state.systems.get(sourceSystemId);
  let pathDistance = 0;
  if (srcSystem) {
    const dx = srcSystem.x - dstSystem.x;
    const dy = srcSystem.y - dstSystem.y;
    pathDistance = Math.sqrt(dx * dx + dy * dy);
    const rangeMul = hyperdriveRangeMultiplier(state, empire);
    let fleetRange = Number.POSITIVE_INFINITY;
    for (const e of entries) {
      const tmpl = state.shipTemplates.get(
        `${empire.raceId}:${e.ship.chassis}`,
      );
      if (!tmpl) continue;
      const shipRange = tmpl.hyperdriveRange * rangeMul;
      if (shipRange < fleetRange) fleetRange = shipRange;
    }
    // A fleet of all stationary platforms (filtered out at line 1239)
    // would leave fleetRange at +Infinity — entries.length === 0 already
    // returned earlier, so reaching here implies at least one mobile
    // ship with a real range.
    if (pathDistance > fleetRange) {
      pushRejection(
        state,
        'jumpShips',
        empireId,
        `Cannot jump — destination ${pathDistance.toFixed(2)} units away, ` +
          `fleet range ${fleetRange.toFixed(2)}.`,
      );
      return;
    }
  }

  // v4M5.2b — distance-scaled fuel cost per `HYPERSPACE.md §3`:
  // cost = Σ over fleet of (ship.hyperdriveFuelUsePerUnit × pathDistance × empireMul)
  let cost = 0;
  for (const e of entries) cost += e.fuelUsePerUnit;
  cost *= pathDistance * hyperdriveCostMultiplier(state, empire);
  if (empire.resources.fuel < cost) {
    pushRejection(
      state,
      'jumpShips',
      empireId,
      `Not enough fuel to jump (need ${Math.ceil(cost)}).`,
    );
    return;
  }

  // v3M18 — auto-mesh removed. JP-based jumps use the standard
  // per-template `jumpDurationTicks` unconditionally; the new
  // player-authored Warp Gate connections are routed through the
  // dedicated jumpShipsViaWarpGate path (see v3M18.4) rather than as
  // a modifier on this code path.

  // v3M17.5 — auto-recall pre-emption: any jumping carrier with deployed
  // squadrons recalls them before the jump fires. Surviving fighters
  // credit to the carrier's rebuild accumulator (squadrons can't enter
  // hyperspace per the v3M17.0 design lock).
  preJumpRecallCarrierSquadrons(state, entries.map((e) => e.ship));

  // All checks passed — mutate.
  empire.resources.fuel -= cost;
  // v4M5.3a — per-ship fuel cost stamped so the abort-refund path
  // (despawnShip) can credit the correct amount on charge-up
  // interruption. Batch cost is fleet-summed but per-ship refund
  // semantics require the per-ship slice (`fuelUsePerUnit × distance ×
  // empireMul`) — same shape as the batch sum, just unaggregated.
  const speedMul = hyperdriveSpeedMultiplier(state, empire);
  const empireCostMul = hyperdriveCostMultiplier(state, empire);
  // v4M5.3a — duration = chargeUp + travel split into two phases:
  //   1. On-map at the JP location for `chargeUp` ticks (ship targetable;
  //      destruction here aborts the jump and refunds `jumpFuelCostPaid`).
  //   2. In hyperspace (systemId === null) for `travel` ticks.
  // The advance pass decrements `chargeUpTicksRemaining` first, then
  // transitions the ship to systemId=null when it reaches 0.
  // v3M18 — auto-mesh removed. JP-based jumps use the standard
  // per-template `jumpDurationTicks` unconditionally. Player-authored
  // Warp Gate connections are routed through the dedicated
  // jumpShipsViaWarpGate path (see v3M18.4) rather than as a modifier
  // on this code path.
  for (const e of entries) {
    const travel = travelTicksFor(pathDistance, e.hyperdriveSpeed, speedMul);
    const duration = e.chargeUpTicks + travel;
    const perShipFuel = e.fuelUsePerUnit * pathDistance * empireCostMul;
    e.ship.jumpOriginSystemId = e.ship.systemId;
    e.ship.jumpTargetSystemId = targetSystemId;
    e.ship.jumpArrivalAngle = arrivalAngle;
    e.ship.jumpTargetGateId = null; // continuous Jump Area never lands at a gate
    e.ship.chargeUpTicksRemaining = e.chargeUpTicks;
    e.ship.jumpTicksRemaining = travel;
    e.ship.jumpDurationTotal = duration;
    e.ship.jumpFuelCostPaid = perShipFuel;
    // v4M5.3a — ship stays on-map at its current (sx, sy) during
    // charge-up. The systemId clears later in `advanceShipJumps` when
    // chargeUpTicksRemaining reaches 0.
    // Any outstanding sublight order is void once the ship leaves the system.
    e.ship.moveOrder = null;
    // Leaving the system aborts any in-flight Constructor build (M14) —
    // no refund, partial work is sunk per STRUCTURES §3.
    e.ship.constructionOrder = null;
    // Leaving the system also drops any active bombardment (v2M5) — the
    // ship can't bombard a planet from another system.
    e.ship.bombardOrder = null;
    // And any pending colonisation (MFix3.4) — the planet is in the
    // origin system, the ship is now headed elsewhere.
    e.ship.pendingColoniseTargetId = null;
    // v3M18 — and any queued warp-link auto-jump.
    e.ship.pendingWarpGateLinkId = null;
    // v3M22.5 — cancel any high-level player command on jump (preserves
    // 'explore' so the explorer's self-issued chain of jumps doesn't
    // self-cancel after the first hop).
    cancelShipCommandOnJump(e.ship);
  }
}

/**
 * Default spacing between formation slots, in local in-system coords. Chosen
 * so a group of ~10 ships fans out over a visually reasonable area (roughly
 * the same order of magnitude as one planet's orbit ring).
 *
 * v3M10.1: migrated to state.balanceData.movement.formationSpacing.
 */

/**
 * Produce `count` formation offsets from a shared target position. Slot 0 is
 * the centre; subsequent slots spiral outward by the golden angle so the
 * pattern stays compact and visually pleasant for any count. Pure — identical
 * calls return identical arrays, which is what the sim relies on for
 * determinism.
 */
export function formationOffsets(
  count: number,
  spacing: number,
): Array<{ dx: number; dy: number }> {
  const out: Array<{ dx: number; dy: number }> = [];
  // Golden angle in radians (~137.508°). Produces Vogel spiral — even packing
  // with no repeating structure, independent of count.
  const GOLDEN = Math.PI * (3 - Math.sqrt(5));
  for (let i = 0; i < count; i++) {
    if (i === 0) {
      out.push({ dx: 0, dy: 0 });
      continue;
    }
    const r = spacing * Math.sqrt(i);
    const a = i * GOLDEN;
    out.push({ dx: Math.cos(a) * r, dy: Math.sin(a) * r });
  }
  return out;
}

/**
 * Validate + apply a sublight move order for a batch of ships. Silent no-op on
 * every failure mode so a malformed/stale command can't corrupt sim state.
 *
 * Validation chain (whole-batch atomic):
 *   1. At least one ship resolves.
 *   2. All resolved ships share one owner empire.
 *   3. All resolved ships are in the same system and not mid-jump.
 * On success: sort ship ids ascending for deterministic formation assignment,
 * generate one offset per ship via formationOffsets(), write per-ship move
 * orders at (targetX+dx, targetY+dy). Offsets are applied even for count=1
 * (a single ship goes straight to the target, since slot 0 is (0,0)).
 */
function applyMoveShips(
  state: GameState,
  shipIds: readonly ShipInstanceId[],
  targetX: number,
  targetY: number,
): void {
  if (shipIds.length === 0) return;

  // Resolve and validate.
  const ships: ShipInstance[] = [];
  let empireId: EmpireId | null = null;
  let systemId: number | null = null;
  for (const id of shipIds) {
    const ship = state.ships.get(id);
    if (!ship) return;
    if (ship.systemId === null) return; // mid-jump
    if (ship.jumpTargetSystemId !== null) return; // also mid-jump (defensive)
    if (empireId === null) empireId = ship.ownerEmpireId;
    else if (empireId !== ship.ownerEmpireId) return; // mixed owners
    if (systemId === null) systemId = ship.systemId;
    else if (systemId !== ship.systemId) return; // split across systems
    // MFix5.2 — skip stationary platforms; mixed-batch keeps the
    // mobile ships moving.
    if (isStationaryPlatform(state, ship)) continue;
    ships.push(ship);
  }
  if (ships.length === 0) return;

  // Deterministic slot assignment: sort ascending by id so the nth-lowest id
  // always gets the nth formation slot.
  ships.sort((a, b) => a.id - b.id);
  const offsets = formationOffsets(ships.length, state.balanceData.movement.formationSpacing);
  for (let i = 0; i < ships.length; i++) {
    const o = offsets[i]!;
    ships[i]!.moveOrder = {
      targetX: targetX + o.dx,
      targetY: targetY + o.dy,
    };
    // Plain move cancels any queued auto-jump — if the player issued a
    // RMB-destination-system order then reconsiders with a normal
    // RMB-to-space order, the second order wins and the ship won't
    // silently jump on arrival.
    ships[i]!.pendingJumpSystemId = null;
    ships[i]!.pendingJumpArrivalAngle = null;
    // v3M18 — and any queued warp-link auto-jump.
    ships[i]!.pendingWarpGateLinkId = null;
    // Same for any queued auto-colonise (MFix3.4).
    ships[i]!.pendingColoniseTargetId = null;
    // New destination also aborts an in-flight construction (M14) — the
    // Constructor is leaving the build site, so the partial work is sunk.
    // No refund, matching STRUCTURES §3 "destroyed-mid-build" semantics.
    ships[i]!.constructionOrder = null;
    // A new move order from the player also clears any active bombardment
    // (v2M5) — the player has redirected the ship away from the orbit.
    ships[i]!.bombardOrder = null;
  }
}

/**
 * Validate + apply a "sail to the source-system perimeter, then jump to
 * the destination system" order. v4M5.3b3a — continuous Jump Area model
 * per `HYPERSPACE.md §4`; no fixed JPs. Same atomic validation as
 * applyMoveShips plus:
 *   - Destination system exists.
 *   - All ships share a single source system (the first ship's system).
 *   - Source system !== destination system.
 *   - Fleet effective range covers the source→destination distance.
 *
 * On success each ship gets a sublight moveOrder at the source system's
 * r=0.48 perimeter facing the destination (with formation offsets), plus
 * `pendingJumpSystemId = targetSystemId` and
 * `pendingJumpArrivalAngle = arrivalAngle`. `tryStartPendingJump` fires
 * the jump on arrival; fuel is checked at fire time so queued jumps don't
 * block on current reserves.
 */
function applyMoveAndJumpShips(
  state: GameState,
  shipIds: readonly ShipInstanceId[],
  targetSystemId: SystemId,
  arrivalAngle: number | null,
): void {
  if (shipIds.length === 0) return;
  // v4M5.3b3a — continuous Jump Area: destination system identity
  // comes from the command; source system is resolved from the ships.
  const dstSystem = state.systems.get(targetSystemId);
  if (!dstSystem) return;

  // Resolve and validate — same chain as applyMoveShips. Source system
  // is pinned to the first ship's system; mixed-system batches are
  // silently rejected.
  const ships: ShipInstance[] = [];
  let empireId: EmpireId | null = null;
  let sourceSystemId: SystemId | null = null;
  for (const id of shipIds) {
    const ship = state.ships.get(id);
    if (!ship) return;
    if (ship.systemId === null) return; // mid-jump
    if (ship.jumpTargetSystemId !== null) return; // mid-jump (defensive)
    if (sourceSystemId === null) sourceSystemId = ship.systemId;
    else if (sourceSystemId !== ship.systemId) return; // mixed source systems
    if (empireId === null) empireId = ship.ownerEmpireId;
    else if (empireId !== ship.ownerEmpireId) return; // mixed owners
    // MFix5.2 — skip stationary platforms; mixed-batch keeps the
    // mobile ships sailing to the JP for auto-jump.
    if (isStationaryPlatform(state, ship)) continue;
    ships.push(ship);
  }
  if (ships.length === 0) return;

  ships.sort((a, b) => a.id - b.id);

  // v4M5.2a — pre-emptive range check at order-issue per
  // `docs/HYPERSPACE.md §1.1`. If the destination is beyond the fleet's
  // effective range we reject here rather than letting the ships sail
  // to the perimeter only to fail the jump at arrival. Same min-over-fleet
  // formula as applyJumpShips so both order types behave identically.
  if (sourceSystemId === targetSystemId) return; // can't jump to your own system
  if (empireId !== null && sourceSystemId !== null) {
    const empireForRange = state.empires.get(empireId);
    const srcSystem = state.systems.get(sourceSystemId);
    if (empireForRange && srcSystem) {
      const dx = srcSystem.x - dstSystem.x;
      const dy = srcSystem.y - dstSystem.y;
      const pathDistance = Math.sqrt(dx * dx + dy * dy);
      const rangeMul = hyperdriveRangeMultiplier(state, empireForRange);
      let fleetRange = Number.POSITIVE_INFINITY;
      for (const ship of ships) {
        const tmpl = state.shipTemplates.get(
          `${empireForRange.raceId}:${ship.chassis}`,
        );
        if (!tmpl) continue;
        const shipRange = tmpl.hyperdriveRange * rangeMul;
        if (shipRange < fleetRange) fleetRange = shipRange;
      }
      if (pathDistance > fleetRange) {
        pushRejection(
          state,
          'moveAndJumpShips',
          empireId,
          `Cannot jump — destination ${pathDistance.toFixed(2)} units away, ` +
            `fleet range ${fleetRange.toFixed(2)}.`,
        );
        return;
      }
    }
  }

  // v3M17.5 — auto-recall pre-emption: any jumping carrier with deployed
  // squadrons recalls them before the sail-then-jump order is stamped.
  // Squadrons can't enter hyperspace, and stranding them at the source
  // system would leave un-rebuildable orphans (since the carrier is on
  // its way out).
  preJumpRecallCarrierSquadrons(state, ships);
  // v4M5.3b3a — sublight sail target is the source system's r=0.48
  // perimeter point facing the destination ("departure side" of the
  // Jump Area ring). Computed in-system from the galaxy-frame direction
  // src→dst. Falls back to centre if either system position is missing.
  const srcSystem =
    sourceSystemId !== null ? state.systems.get(sourceSystemId) : undefined;
  let depX = 0.5;
  let depY = 0.5;
  if (srcSystem) {
    const ddx = dstSystem.x - srcSystem.x;
    const ddy = dstSystem.y - srcSystem.y;
    const depAngle = Math.atan2(ddy, ddx);
    depX = 0.5 + 0.48 * Math.cos(depAngle);
    depY = 0.5 + 0.48 * Math.sin(depAngle);
  }
  const offsets = formationOffsets(ships.length, state.balanceData.movement.formationSpacing);
  for (let i = 0; i < ships.length; i++) {
    const o = offsets[i]!;
    ships[i]!.moveOrder = {
      targetX: depX + o.dx,
      targetY: depY + o.dy,
    };
    ships[i]!.pendingJumpSystemId = targetSystemId;
    ships[i]!.pendingJumpArrivalAngle = arrivalAngle;
    ships[i]!.pendingWarpGateLinkId = null; // moveAndJump supersedes warp-link queue
    ships[i]!.pendingColoniseTargetId = null; // moveAndJump supersedes any pending colonise (MFix3.4)
    ships[i]!.constructionOrder = null; // abandon in-flight build (M14)
    ships[i]!.bombardOrder = null; // abandon active bombardment (v2M5)
  }
}

// ---------------------------------------------------------------------------
// Terraform (MFix4.3)
// ---------------------------------------------------------------------------

/**
 * Validate and commit a terraform start. Pure mutation; silent no-op on
 * any failure. Two paths share the same applier with the
 * `coloniserShipId` discriminator: non-null = Terraforming L4 path
 * (Coloniser at uninhabited planet, ship consumed); null = Advanced
 * Terraforming L6 path (own planet, no ship).
 *
 * Exported so tests can drive both paths without going through `applyCommand`.
 */
export function applyStartTerraform(
  state: GameState,
  empireId: EmpireId,
  targetPlanetId: PlanetId,
  coloniserShipId: ShipInstanceId | null,
): void {
  const empire = state.empires.get(empireId);
  if (!empire) return;
  const planet = state.planets.get(targetPlanetId);
  if (!planet) return;

  // Never terraform a Gaia planet (target IS Gaia, no-op).
  if (planet.biome === 'gaia') return;
  // Never restart an in-flight terraform.
  if (planet.terraforming !== null) return;

  if (coloniserShipId !== null) {
    // ── Coloniser path (Terraforming L4) ─────────────────────────────
    if (!empire.researchedTechs.has(TERRAFORMING_TECH)) return;
    if (planet.ownerEmpireId !== null) return;
    if (planet.currentPopulation > 0) return;

    const ship = state.ships.get(coloniserShipId);
    if (!ship) return;
    if (ship.ownerEmpireId !== empireId) return;
    if (ship.systemId === null) return; // mid-jump
    if (ship.systemId !== planet.systemId) return;

    const tmpl = state.shipTemplates.get(`${empire.raceId}:${ship.chassis}`);
    if (!tmpl) return;
    if (!tmpl.specialAbilities.includes(COLONISER_SPECIAL_ABILITY)) return;

    const range = colonisationRangeFor(planet.size, state.balanceData);
    const dx = ship.sx - planet.systemX;
    const dy = ship.sy - planet.systemY;
    if (dx * dx + dy * dy > range * range) return;

    if (empire.resources.minerals < state.balanceData.terraforming.mineralsCost) return;
    if (empire.resources.fuel < state.balanceData.terraforming.fuelCost) return;

    empire.resources.minerals -= state.balanceData.terraforming.mineralsCost;
    empire.resources.fuel -= state.balanceData.terraforming.fuelCost;
    planet.terraforming = {
      targetBiome: 'gaia',
      ticksRemaining: state.balanceData.terraforming.durationTicks,
      fuelCost: state.balanceData.terraforming.fuelCost,
      mineralsCost: state.balanceData.terraforming.mineralsCost,
    };
    despawnShip(state, coloniserShipId);
    return;
  }

  // ── Planet-only path (Advanced Terraforming L6) ───────────────────
  if (!empire.researchedTechs.has(ADVANCED_TERRAFORMING_TECH)) return;
  if (planet.ownerEmpireId !== empireId) return;

  if (empire.resources.minerals < state.balanceData.terraforming.mineralsCost) return;
  if (empire.resources.fuel < state.balanceData.terraforming.fuelCost) return;

  empire.resources.minerals -= state.balanceData.terraforming.mineralsCost;
  empire.resources.fuel -= state.balanceData.terraforming.fuelCost;
  planet.terraforming = {
    targetBiome: 'gaia',
    ticksRemaining: state.balanceData.terraforming.durationTicks,
    fuelCost: state.balanceData.terraforming.fuelCost,
    mineralsCost: state.balanceData.terraforming.mineralsCost,
  };
}

// ---------------------------------------------------------------------------
// MoveAndColonise (MFix3.4)
// ---------------------------------------------------------------------------

/**
 * Validate + queue a sail-then-colonise order on a single Coloniser-class
 * ship. Pure mutation; silent no-op on any failure. The colonise itself
 * commits later when `advancePendingColonisations` notices arrival.
 */
function applyMoveAndColonise(
  state: GameState,
  empireId: EmpireId,
  shipId: ShipInstanceId,
  targetPlanetId: PlanetId,
): void {
  const empire = state.empires.get(empireId);
  if (!empire) return;
  const ship = state.ships.get(shipId);
  if (!ship) return;
  if (ship.ownerEmpireId !== empireId) return;
  if (ship.systemId === null) return; // mid-jump
  if (ship.jumpTargetSystemId !== null) return; // mid-jump (defensive)
  const planet = state.planets.get(targetPlanetId);
  if (!planet) return;
  if (planet.systemId !== ship.systemId) return;

  const tmpl = state.shipTemplates.get(`${empire.raceId}:${ship.chassis}`);
  if (!tmpl) return;
  if (!tmpl.specialAbilities.includes(COLONISER_SPECIAL_ABILITY)) return;

  // Queue the move + pending colonise. The colonise validation chain
  // (Settlement tech, planet uninhabited, range) runs at arrival, not here.
  ship.moveOrder = { targetX: planet.systemX, targetY: planet.systemY };
  ship.pendingColoniseTargetId = targetPlanetId;
  ship.pendingJumpSystemId = null; // mutually exclusive with auto-jump
  ship.pendingJumpArrivalAngle = null;
  ship.pendingWarpGateLinkId = null; // and warp-link auto-jump (v3M18)
  ship.constructionOrder = null; // abandon in-flight build (M14)
  ship.bombardOrder = null; // abandon active bombardment (v2M5)
}


/**
 * Validate + apply a cloak toggle. Group semantics:
 *   - Filter `shipIds` down to *eligible* ships: owned by `empireId`, template
 *     `canCloak`, empire has researched `CLOAKING_TECH_ID`, not mid-jump, not
 *     EMP-disabled.
 *   - If any eligible ship is currently cloaked, decloak ALL eligible.
 *   - Otherwise, cloak every eligible ship whose `currentEnergy` covers at
 *     least one tick of drain (so cloak doesn't flicker on immediately).
 *
 * Silent skip for non-eligible ships — a mixed selection containing one enemy
 * scout and several friendly cruisers just cloaks the friendlies. No partial-
 * batch failure path: every failure mode is "do nothing for this ship".
 */
function applyToggleCloak(
  state: GameState,
  shipIds: readonly ShipInstanceId[],
  empireId: EmpireId,
): void {
  const empire = state.empires.get(empireId);
  if (!empire) return;
  if (!empire.researchedTechs.has(CLOAKING_TECH_ID)) {
    pushRejection(
      state,
      'toggleCloak',
      empireId,
      `Cloaking requires the ${CLOAKING_TECH_ID} tech.`,
    );
    return;
  }

  const eligible: ShipInstance[] = [];
  for (const id of shipIds) {
    const ship = state.ships.get(id);
    if (!ship) continue;
    if (ship.ownerEmpireId !== empireId) continue;
    if (ship.systemId === null) continue; // mid-jump
    if (ship.disabledTicks > 0) continue; // EMP'd
    const tmpl = state.shipTemplates.get(`${empire.raceId}:${ship.chassis}`);
    if (!tmpl) continue;
    if (!tmpl.canCloak) continue;
    eligible.push(ship);
  }
  if (eligible.length === 0) return;

  const anyCloaked = eligible.some((s) => s.cloaked);
  if (anyCloaked) {
    for (const ship of eligible) ship.cloaked = false;
    return;
  }
  // Going cloaked — require enough energy to survive at least one drain tick.
  for (const ship of eligible) {
    if (ship.currentEnergy >= state.balanceData.detection.cloakEnergyDrainPerTick) {
      ship.cloaked = true;
      // Cloaking aborts a Constructor's build (M14). Stealth and overt
      // on-site construction are incompatible; if the Constructor goes
      // dark, the partial work is lost per STRUCTURES §3.
      ship.constructionOrder = null;
      // Cloaking also aborts active bombardment (v2M5) — firing at a
      // planet would break stealth anyway; cleaner to drop the order.
      ship.bombardOrder = null;
    }
  }
}

/**
 * Validate + start a new structure build on a planet (STRUCTURES §2 / §4).
 *
 * Validation chain (silent no-op on failure):
 *   1. Empire and planet exist.
 *   2. Planet is owned by the issuing empire (can't build on enemy or
 *      neutral turf).
 *   3. Planet has a free structure slot (maxStructuresForSize).
 *   4. If `isUniqueKind(structuresData, kind)`, no existing structure on
 *      this planet has the same kind.
 *   5. Empire has the flat per-level build cost (credits + minerals).
 *
 * On success: allocate a new Structure at level 0, push it onto the planet's
 * `structureIds`, deduct the cost, set `buildTicksRemaining` to the initial
 * build time. Hull fields carry the level-1 ceiling from the start — a
 * structure under construction is invulnerable in v1 (no bombardment), so
 * tracking hull from tick zero keeps advance-structures.ts branch-free.
 */
function applyBuildStructure(
  state: GameState,
  empireId: EmpireId,
  planetId: PlanetId,
  structureKind: StructureKind,
): void {
  const empire = state.empires.get(empireId);
  if (!empire) return;
  const planet = state.planets.get(planetId);
  if (!planet) return;
  if (planet.ownerEmpireId !== empireId) return;

  const slotCap = maxStructuresForSize(planet.size);
  if (planet.structureIds.length >= slotCap) {
    pushRejection(
      state,
      'buildStructure',
      empireId,
      `${planet.name} has no free structure slots (cap ${slotCap}).`,
    );
    return;
  }

  const sd = state.structuresData;
  if (isUniqueKind(sd, structureKind)) {
    for (const id of planet.structureIds) {
      const existing = state.structures.get(id);
      if (existing && existing.kind === structureKind) {
        pushRejection(
          state,
          'buildStructure',
          empireId,
          `${planet.name} already has a ${structureKind}.`,
        );
        return;
      }
    }
  }

  const credits = buildCreditsForKind(sd, structureKind);
  const minerals = buildMineralsForKind(sd, structureKind);
  if (empire.resources.credits < credits) {
    pushRejection(
      state,
      'buildStructure',
      empireId,
      `Not enough credits to build ${structureKind} (need ${credits}).`,
    );
    return;
  }
  if (empire.resources.minerals < minerals) {
    pushRejection(
      state,
      'buildStructure',
      empireId,
      `Not enough minerals to build ${structureKind} (need ${minerals}).`,
    );
    return;
  }

  const hull = maxHullForLevel(sd, structureKind, 1);
  const structure: Structure = {
    id: allocEntityId(state),
    planetId,
    kind: structureKind,
    level: 0,
    buildTicksRemaining: buildTicksForLevel(sd, structureKind, 1),
    queuedAt: state.tick,
    currentHull: hull,
    maxHull: hull,
    weaponCooldownTicks: 0,
  };
  state.structures.set(structure.id, structure);
  planet.structureIds.push(structure.id);
  empire.resources.credits -= credits;
  empire.resources.minerals -= minerals;
}

/**
 * Validate + start an upgrade on an existing structure (STRUCTURES §4).
 *
 * Validation chain (silent no-op on failure):
 *   1. Empire, structure, and owning planet exist.
 *   2. Planet is owned by the issuing empire.
 *   3. Structure is fully built (level >= 1) and below the cap (level < 6).
 *   4. Structure is not already mid-upgrade (buildTicksRemaining === 0).
 *   5. Empire has the flat per-level upgrade cost.
 *
 * On success: deduct cost, set `buildTicksRemaining` to the time-to-next-level
 * from `buildTicksForLevel(structuresData, kind, level + 1)`. The structure's current `level` stays
 * unchanged — current-tier effects remain live during the upgrade. The level
 * promotes to `level + 1` when advance-structures.ts decrements the
 * countdown to 0 (refitting hull to the new level's ceiling).
 */
function applyUpgradeStructure(
  state: GameState,
  empireId: EmpireId,
  structureId: EntityId,
): void {
  const empire = state.empires.get(empireId);
  if (!empire) return;
  const structure = state.structures.get(structureId);
  if (!structure) return;
  const planet = state.planets.get(structure.planetId);
  if (!planet) return;
  if (planet.ownerEmpireId !== empireId) return;
  // MFix5.3 — race-gated upgrade cap (Embassy: RACE-AA L2, BB/CC L1).
  // Falls back to the kind-level cap when no race override exists.
  const sd = state.structuresData;
  const maxLevel = maxLevelForKindByRace(sd, structure.kind, empire.raceId);
  if (structure.level < 1) {
    pushRejection(
      state,
      'upgradeStructure',
      empireId,
      `${structure.kind} on ${planet.name} is still under construction.`,
    );
    return;
  }
  if (structure.level >= maxLevel) {
    pushRejection(
      state,
      'upgradeStructure',
      empireId,
      `${structure.kind} on ${planet.name} is already at max level (${maxLevel}).`,
    );
    return;
  }
  if (structure.buildTicksRemaining > 0) {
    pushRejection(
      state,
      'upgradeStructure',
      empireId,
      `${structure.kind} on ${planet.name} is already upgrading.`,
    );
    return;
  }
  const credits = buildCreditsForKind(sd, structure.kind);
  const minerals = buildMineralsForKind(sd, structure.kind);
  if (empire.resources.credits < credits) {
    pushRejection(
      state,
      'upgradeStructure',
      empireId,
      `Not enough credits to upgrade ${structure.kind} (need ${credits}).`,
    );
    return;
  }
  if (empire.resources.minerals < minerals) {
    pushRejection(
      state,
      'upgradeStructure',
      empireId,
      `Not enough minerals to upgrade ${structure.kind} (need ${minerals}).`,
    );
    return;
  }

  empire.resources.credits -= credits;
  empire.resources.minerals -= minerals;
  // BUILD_TICKS_FOR_LEVEL[level] in the old code = ticks for the (level+1)th
  // upgrade step. With the new helper we ask for "ticks to reach level + 1"
  // directly — same semantics, clearer at the call site.
  structure.buildTicksRemaining = buildTicksForLevel(sd, structure.kind, structure.level + 1);
  structure.queuedAt = state.tick;
}

/**
 * Queue a ship build on a planet (M10).
 *
 * Validation chain (silent no-op on failure, same idiom as the other
 * command appliers):
 *   1. Empire and planet exist.
 *   2. Planet is owned by the issuing empire.
 *   3. Chassis resolves to a ShipTemplate for the empire's race. Races
 *      can't build each other's chassis (race-gated rosters in Ships.xlsx).
 *   4. The planet has a Shipyard at level >= the chassis's ClassLevel —
 *      canBuildClassAtPlanet wraps the check consistently for UI + sim.
 *   5. Empire has credits + minerals for the template's build cost, and
 *      the planet has enough population to cover popBuildCost.
 *
 * On success: deduct credits + minerals from the empire, pop from the
 * planet, and push a ShipBuildOrder onto the planet's queue with
 * buildTicksRemaining sourced from BUILD_TICKS_BY_CLASS. Order executes
 * in FIFO order — advance-shipyard.ts will tick queue[0] and spawn the
 * ship on completion.
 */
function applyBuildShip(
  state: GameState,
  empireId: EmpireId,
  planetId: PlanetId,
  chassis: string,
): void {
  const empire = state.empires.get(empireId);
  if (!empire) return;
  const planet = state.planets.get(planetId);
  if (!planet) return;
  if (planet.ownerEmpireId !== empireId) return;

  const tmpl = state.shipTemplates.get(`${empire.raceId}:${chassis}`);
  if (!tmpl) return;

  // MFix7.3 — Stationary templates (M14 space structures + the synthetic
  // 'ODP Platform' ring chassis) are never planet-buildable. Defensive
  // belt-and-braces: the Shipyard UI already filters these out, but this
  // guards against direct command injection or any future UI path that
  // misses the filter.
  if (tmpl.specialAbilities.includes(STATIONARY_SPECIAL_ABILITY)) return;
  // v3M17.1 — Synthetic squadron chassis (Corvette / Fighter / Infiltrator
  // Squadron) are spawned by the carrier launch path, never planet-built.
  // Same belt-and-braces pattern as the Stationary guard above.
  if (SQUADRON_CHASSIS_NAMES.has(chassis)) return;

  if (!canBuildClassAtPlanet(state, planetId, tmpl.classLevel)) {
    pushRejection(
      state,
      'buildShip',
      empireId,
      `${planet.name} cannot build Class ${tmpl.classLevel} ships — shipyard upgrade required.`,
    );
    return;
  }

  // Chassis tech gate (M12.3). Class 1 is always-available (implicit
  // baseline, matches the Mk 1 pattern across other ladders). Class 2..6
  // require the corresponding "Class N Starship Chassis" tech. Names are
  // deterministic per Tech_Tree.xlsx.
  if (tmpl.classLevel >= 2) {
    const chassisTech = `Class ${tmpl.classLevel} Starship Chassis`;
    if (!empire.researchedTechs.has(chassisTech)) {
      pushRejection(
        state,
        'buildShip',
        empireId,
        `${chassis} requires the ${chassisTech} tech.`,
      );
      return;
    }
  }

  if (empire.resources.credits < tmpl.creditsBuildCost) {
    pushRejection(
      state,
      'buildShip',
      empireId,
      `Not enough credits to build ${chassis} (need ${tmpl.creditsBuildCost}).`,
    );
    return;
  }
  if (empire.resources.minerals < tmpl.mineralsBuildCost) {
    pushRejection(
      state,
      'buildShip',
      empireId,
      `Not enough minerals to build ${chassis} (need ${tmpl.mineralsBuildCost}).`,
    );
    return;
  }
  if (planet.currentPopulation < tmpl.popBuildCost) {
    pushRejection(
      state,
      'buildShip',
      empireId,
      `${planet.name} lacks population to build ${chassis} (need ${tmpl.popBuildCost}).`,
    );
    return;
  }

  empire.resources.credits -= tmpl.creditsBuildCost;
  empire.resources.minerals -= tmpl.mineralsBuildCost;
  planet.currentPopulation -= tmpl.popBuildCost;

  const ticks = buildTicksByClass(state.balanceData)[tmpl.classLevel];
  planet.shipBuildQueue.push({
    chassis,
    buildTicksRemaining: ticks,
    buildTicksTotal: ticks,
  });
}

/**
 * Set or clear an empire's active research target (M12).
 *
 * Validation chain (silent no-op on any failure):
 *   1. Empire exists.
 *   2. If techName is null → clear active target unconditionally.
 *   3. Otherwise: tech exists, not already in researchedTechs, race gate
 *      ('All' or exact match), every prerequisite already researched.
 *   4. Setting the SAME techName as the current target is a no-op (so
 *      spurious re-sets don't reset rpAccumulated).
 *   5. Setting a DIFFERENT techName discards any previous progress and
 *      starts a fresh project at rpAccumulated = 0.
 */
function applySetResearchTarget(
  state: GameState,
  empireId: EmpireId,
  techName: string | null,
): void {
  const empire = state.empires.get(empireId);
  if (!empire) return;

  if (techName === null) {
    empire.activeResearch = null;
    return;
  }

  const tech = state.techs.get(techName);
  if (!tech) return;
  if (empire.researchedTechs.has(techName)) return;
  if (tech.race !== 'All' && tech.race !== empire.raceId) return;
  for (const prereq of tech.prerequisites) {
    if (!empire.researchedTechs.has(prereq)) return;
  }

  if (empire.activeResearch !== null && empire.activeResearch.techName === techName) {
    return; // already targeting this — preserve rpAccumulated
  }
  empire.activeResearch = { techName, rpAccumulated: 0 };
}

/**
 * Start a space-structure build on a Constructor ship (M14 / STRUCTURES §3).
 *
 * Validation chain (silent no-op on any failure):
 *   1. Empire and ship exist; ship is owned by the empire.
 *   2. Ship is this empire's Constructor chassis (per
 *      CONSTRUCTOR_CHASSIS_BY_RACE — each race has one Constructor name).
 *   3. Ship is in-system, not mid-jump, not EMP-disabled, not cloaked,
 *      not already constructing.
 *   4. structureKind is a recognised space structure (SPACE_STRUCTURE_KINDS).
 *   5. If the kind requires a site (Asteroid Mining Outpost → asteroid
 *      field; Gas Giant Refinery → gas giant), there's a matching
 *      environment entity under the Constructor's current (sx, sy).
 *   6. Empire has the build cost in credits + minerals.
 *
 * On success: deduct cost up front (no refund on cancel / destruction per
 * STRUCTURES §3), stamp `constructionOrder` on the Constructor with the
 * target kind + ticks. `advance-construction.ts` ticks the countdown and
 * spawns the structure at the Constructor's position on completion.
 */
function applyBuildSpaceStructure(
  state: GameState,
  empireId: EmpireId,
  constructorShipId: ShipInstanceId,
  structureKind: string,
  sx: number,
  sy: number,
): void {
  const empire = state.empires.get(empireId);
  if (!empire) return;
  const ship = state.ships.get(constructorShipId);
  if (!ship) return;
  if (ship.ownerEmpireId !== empireId) return;

  const constructorChassis = CONSTRUCTOR_CHASSIS_BY_RACE[empire.raceId];
  if (ship.chassis !== constructorChassis) return;
  if (ship.systemId === null) return;
  if (ship.disabledTicks > 0) {
    pushRejection(
      state,
      'buildSpaceStructure',
      empireId,
      `Constructor is EMP-disabled and cannot build.`,
    );
    return;
  }
  if (ship.cloaked) {
    pushRejection(
      state,
      'buildSpaceStructure',
      empireId,
      `Constructor must decloak before building.`,
    );
    return;
  }
  if (ship.constructionOrder !== null) {
    pushRejection(
      state,
      'buildSpaceStructure',
      empireId,
      `Constructor is already building.`,
    );
    return;
  }

  if (!SPACE_STRUCTURE_KINDS.includes(structureKind)) return;

  // Target must be within the [0, 1] in-system frame. Defensive against
  // bad UI input.
  if (sx < 0 || sx > 1 || sy < 0 || sy > 1) return;

  // Site validation now fires at the TARGET coords, not the Constructor's
  // current position — the player picked a specific build location.
  if (!isValidBuildSite(state, ship.systemId, structureKind, sx, sy)) {
    pushRejection(
      state,
      'buildSpaceStructure',
      empireId,
      `Invalid site for ${structureKind} — requires the right environment.`,
    );
    return;
  }

  // v3M2.1 — Warp Gate one-per-system-per-player. Reject if the empire
  // already owns or is constructing a Warp Gate in this system.
  if (structureKind === WARP_GATE_CHASSIS) {
    if (empireOwnsWarpGateInSystem(state, empireId, ship.systemId)) {
      pushRejection(
        state,
        'buildSpaceStructure',
        empireId,
        `You already have a Warp Gate in this system.`,
      );
      return;
    }
  }

  // v3M2.3 — Minefield requires the `Minefields` tech. Constructors of
  // empires without the research silently no-op.
  if (structureKind === MINEFIELD_CHASSIS) {
    if (!empire.researchedTechs.has(MINEFIELDS_TECH)) {
      pushRejection(
        state,
        'buildSpaceStructure',
        empireId,
        `Minefields require the ${MINEFIELDS_TECH} tech.`,
      );
      return;
    }
  }

  const sd = state.structuresData;
  const cost = spaceBuildCostForKind(sd, structureKind);
  const ticks = spaceBuildTicksForKind(sd, structureKind);
  if (cost.credits <= 0 || ticks <= 0) return; // defensive — missing catalogue entry
  if (empire.resources.credits < cost.credits) {
    pushRejection(
      state,
      'buildSpaceStructure',
      empireId,
      `Not enough credits to build ${structureKind} (need ${cost.credits}).`,
    );
    return;
  }
  if (empire.resources.minerals < cost.minerals) {
    pushRejection(
      state,
      'buildSpaceStructure',
      empireId,
      `Not enough minerals to build ${structureKind} (need ${cost.minerals}).`,
    );
    return;
  }

  empire.resources.credits -= cost.credits;
  empire.resources.minerals -= cost.minerals;
  ship.constructionOrder = {
    structureKind,
    sx,
    sy,
    ticksRemaining: ticks,
    ticksTotal: ticks,
  };
  // Direct-set moveOrder (bypass applyMoveShips) so its side-effect of
  // clearing constructionOrder doesn't immediately undo what we just set.
  // advance-shipMovement handles the physical travel and clears moveOrder
  // on arrival; advance-construction begins ticking once moveOrder is null.
  ship.moveOrder = { targetX: sx, targetY: sy };
  // Also clear any pending auto-jump so a previously queued sail
  // doesn't fight the build.
  ship.pendingJumpSystemId = null;
  ship.pendingJumpArrivalAngle = null;
  ship.pendingWarpGateLinkId = null; // v3M18 — warp-link too
  // Same for any pending auto-colonise (MFix3.4) — Constructors aren't
  // colonisers, but the field exists on every ship for type-shape
  // simplicity and a stale value would be a bug.
  ship.pendingColoniseTargetId = null;
  // Clear any active bombardment — constructor-build and bombardment are
  // mutually exclusive (v2M5).
  ship.bombardOrder = null;
}

/**
 * Returns true if `structureKind` can be built at `(sx, sy)` in the given
 * system. Non-site-gated kinds return true unconditionally; Asteroid
 * Mining Outposts need an asteroid-field entity containing the point;
 * Gas Giant Refineries need a gas-giant entity. Exported for UI use —
 * the placement-mode ghost colours itself by calling this each render.
 */
export function isValidBuildSite(
  state: GameState,
  systemId: number,
  structureKind: string,
  sx: number,
  sy: number,
): boolean {
  if (!SPACE_STRUCTURE_KINDS.includes(structureKind)) return false;
  const siteRequired = spaceSiteRequirementForKind(state.structuresData, structureKind);
  if (siteRequired === 'open') return true;
  for (const env of state.environment.values()) {
    if (env.systemId !== systemId) continue;
    if (env.kind !== siteRequired) continue;
    const dx = env.sx - sx;
    const dy = env.sy - sy;
    if (dx * dx + dy * dy <= env.radius * env.radius) return true;
  }
  return false;
}

/**
 * Validate + apply an explicit bombardment order (v2M5, PLANETS §2.7).
 *
 * Semantics:
 *  - Players can bombard enemy and neutral planets but never their own.
 *  - All ships in the order must be in the same system as the target planet
 *    and not mid-jump. Ships in another system can't fire at the planet
 *    (range checks happen tick-by-tick in advance-bombardment.ts).
 *  - On success: each eligible ship gets `bombardOrder = {targetPlanetId}`,
 *    its `constructionOrder` cleared (mutually exclusive), and a `moveOrder`
 *    set to the planet's centre so the ship closes to firing range without
 *    the player needing to issue a separate move. Existing combat behaviour
 *    isn't disturbed — combat still runs first each tick; bombardment
 *    consumes whatever weapon-slot capacity remains.
 *  - Whole-batch atomic: if any ship fails validation, the entire command
 *    is silently rejected. Same model as applyJumpShips / applyMoveShips.
 */
function applyBombardPlanet(
  state: GameState,
  empireId: EmpireId,
  shipIds: readonly ShipInstanceId[],
  targetPlanetId: PlanetId,
): void {
  const empire = state.empires.get(empireId);
  if (!empire) return;
  const planet = state.planets.get(targetPlanetId);
  if (!planet) return;
  // Can't bombard your own world. Neutral (ownerEmpireId === null) is
  // explicitly allowed per PLANETS §2.7.
  if (planet.ownerEmpireId === empireId) {
    pushRejection(
      state,
      'bombardPlanet',
      empireId,
      `Cannot bombard your own planet (${planet.name}).`,
    );
    return;
  }
  // v4M3.3 — friendly-fire block extends to teammates per `TEAMS.md §4`.
  if (planet.ownerEmpireId !== null && areTeammates(state, empireId, planet.ownerEmpireId)) {
    pushRejection(
      state,
      'bombardPlanet',
      empireId,
      `Cannot bombard ${planet.name} — owned by your teammate.`,
    );
    return;
  }
  if (shipIds.length === 0) return;

  // Resolve + validate every ship up front. Whole-batch atomic.
  const ships: ShipInstance[] = [];
  for (const id of shipIds) {
    const ship = state.ships.get(id);
    if (!ship) return;
    if (ship.ownerEmpireId !== empireId) return;
    if (ship.systemId === null) return; // mid-jump
    if (ship.systemId !== planet.systemId) return; // wrong system
    if (ship.jumpTargetSystemId !== null) return; // defensive: queued jump
    ships.push(ship);
  }
  if (ships.length === 0) return;

  for (const ship of ships) {
    ship.bombardOrder = { targetPlanetId };
    // Mutually exclusive with construction; clear if active.
    ship.constructionOrder = null;
    // Set a moveOrder to the planet's centre so the ship closes to firing
    // range. advance-bombardment.ts handles the per-tick range check; once
    // the ship is within `weapon.range + planet_sim_radius` it fires.
    // Clearing pendingJumpSystemId prevents a stale auto-jump cancelling
    // the approach mid-flight.
    ship.moveOrder = { targetX: planet.systemX, targetY: planet.systemY };
    ship.pendingJumpSystemId = null;
    ship.pendingJumpArrivalAngle = null;
    ship.pendingWarpGateLinkId = null; // v3M18 — warp-link too
    ship.pendingColoniseTargetId = null; // bombard supersedes any pending colonise
  }
}

/**
 * v3M12.4 — manual surrender path. Just flips `gameOutcome` to
 * `'defeat'` if the game is still in progress. Doesn't touch ships
 * or planets — Concede is "I give up while I still have things",
 * not "I lost everything." The player can pan around the frozen
 * final state via Continue Playing.
 *
 * Idempotent — re-dispatching after the outcome is already settled
 * is a no-op. Important because the in-game menu's UI race could
 * otherwise let the button be clicked twice.
 */
function applyConcede(state: GameState): void {
  if (state.gameOutcome !== 'in-progress') return;
  state.gameOutcome = 'defeat';
}

// ---------------------------------------------------------------------------
// Warp Gate connect / disconnect (v3M18)
// ---------------------------------------------------------------------------

/**
 * v3M18 — author a Warp Gate connection between two empire-owned
 * gates. Validation chain in the Command jsdoc (silent no-op on any
 * fail). Per CLAUDE.md SOP, exported so tests can drive directly.
 */
export function applyConnectWarpGate(
  state: GameState,
  empireId: EmpireId,
  gateAId: ShipInstanceId,
  gateBId: ShipInstanceId,
): void {
  if (gateAId === gateBId) return;
  const empire = state.empires.get(empireId);
  if (!empire) return;
  const gateA = state.ships.get(gateAId);
  const gateB = state.ships.get(gateBId);
  if (!gateA || !gateB) return;
  if (gateA.ownerEmpireId !== empireId || gateB.ownerEmpireId !== empireId)
    return;
  if (gateA.chassis !== WARP_GATE_CHASSIS || gateB.chassis !== WARP_GATE_CHASSIS)
    return;
  // Operational ⇒ has a systemId. A gate mid-jump has systemId === null;
  // we don't allow connecting in that state — but in v1 gates are
  // stationary, so this guard is defensive.
  if (gateA.systemId === null || gateB.systemId === null) return;
  if (gateA.systemId === gateB.systemId) return;
  if (gateIsLinked(state, gateAId) || gateIsLinked(state, gateBId)) {
    pushRejection(
      state,
      'connectWarpGate',
      empireId,
      `One of the selected gates is already connected.`,
    );
    return;
  }
  const minerals = warpGateConnectionCostMinerals(state.structuresData);
  const fuel = warpGateConnectionCostFuel(state.structuresData);
  if (empire.resources.minerals < minerals) {
    pushRejection(
      state,
      'connectWarpGate',
      empireId,
      `Not enough minerals to open warp link (need ${minerals}).`,
    );
    return;
  }
  if (empire.resources.fuel < fuel) {
    pushRejection(
      state,
      'connectWarpGate',
      empireId,
      `Not enough fuel to open warp link (need ${fuel}).`,
    );
    return;
  }

  // All checks passed — debit resources + create the link.
  empire.resources.minerals -= minerals;
  empire.resources.fuel -= fuel;
  const id = allocEntityId(state);
  state.warpGateLinks.set(id, {
    id,
    ownerEmpireId: empireId,
    gateAId,
    gateBId,
    systemAId: gateA.systemId,
    systemBId: gateB.systemId,
    status: 'building',
    progressTicks: 0,
    targetTicks: warpGateConnectionBuildTicks(state.structuresData),
  });
}

/**
 * v3M18 — drop an existing Warp Gate connection. Instant + free for
 * either status. Caller validates ownership; we re-check defensively
 * so a hostile or stale command can't mutate a foreign empire's link.
 */
export function applyDisconnectWarpGate(
  state: GameState,
  empireId: EmpireId,
  linkId: WarpGateLinkId,
): void {
  const link = state.warpGateLinks.get(linkId);
  if (!link) return;
  if (link.ownerEmpireId !== empireId) return;
  state.warpGateLinks.delete(linkId);
}

/**
 * v3M18 — initiate a warp-link hyperspace jump. Mirrors
 * `applyMoveAndJumpShips` for the warp-link pipeline: ships are given
 * a sublight `moveOrder` aimed at the source-side gate's (sx, sy)
 * plus `pendingWarpGateLinkId` set to the link. The actual hyperdrive
 * fire happens in `tryStartPendingWarpGateJump` (advance.ts) once the
 * ship has arrived at the gate (moveOrder === null). Fuel is checked
 * at fire time, not at issue, so players can queue a warp jump while
 * still mining the cost.
 *
 * Whole-batch atomic validation per the Command jsdoc.
 *
 * The on-arrival duration formula is `max(50, round(galaxyDistance ×
 * tmpl.jumpDurationTicks))` where `galaxyDistance` is the Euclidean
 * distance in galaxy 0..1 coords. Floor of 50 ticks (5 sec at 10 Hz)
 * keeps very-close-systems travel from collapsing to zero.
 */
export function applyJumpShipsViaWarpGate(
  state: GameState,
  shipIds: readonly ShipInstanceId[],
  linkId: WarpGateLinkId,
): void {
  const link = state.warpGateLinks.get(linkId);
  if (!link) return;
  if (link.status !== 'live') return;
  const gateA = state.ships.get(link.gateAId);
  const gateB = state.ships.get(link.gateBId);
  if (!gateA || !gateB) return;

  // Resolve ships and validate in one pass. All ships must share an
  // empire matching the link, and all must sit in the same system
  // (one of the two gate systems).
  const entries: ShipInstance[] = [];
  let empireId: EmpireId | null = null;
  let originSystemId: SystemId | null = null;
  for (const shipId of shipIds) {
    const ship = state.ships.get(shipId);
    if (!ship) return;
    if (ship.systemId === null) return; // mid-jump
    if (ship.jumpTargetSystemId !== null) return; // already mid-jump (defensive)
    if (empireId === null) empireId = ship.ownerEmpireId;
    else if (empireId !== ship.ownerEmpireId) return;
    if (originSystemId === null) originSystemId = ship.systemId;
    else if (originSystemId !== ship.systemId) return;
    if (
      ship.systemId !== link.systemAId &&
      ship.systemId !== link.systemBId
    ) {
      return; // ship not at either endpoint
    }
    const empire = state.empires.get(ship.ownerEmpireId);
    if (!empire) return;
    const tmpl = state.shipTemplates.get(`${empire.raceId}:${ship.chassis}`);
    if (!tmpl) return;
    if (tmpl.sublightSpeed <= 0) continue; // skip stationary platforms
    entries.push(ship);
  }
  if (entries.length === 0 || empireId === null || originSystemId === null) {
    return;
  }
  if (empireId !== link.ownerEmpireId) return; // friendly-only

  // v3M17.5 — auto-recall pre-emption: any jumping carrier with deployed
  // squadrons recalls them before the warp-link sail-then-jump order is
  // stamped. Same rationale as the JP-jump path — squadrons cannot
  // enter hyperspace and stranding them is the wrong UX.
  preJumpRecallCarrierSquadrons(state, entries);

  // Aim every ship at the source-side gate. Formation offsets keep them
  // from stacking on a single point — same Vogel-spiral pattern as
  // applyMoveShips / applyMoveAndJumpShips.
  const sourceGate =
    originSystemId === link.systemAId ? gateA : gateB;
  const offsets = formationOffsets(entries.length, state.balanceData.movement.formationSpacing);
  // Sort by id for deterministic offset assignment.
  entries.sort((a, b) => a.id - b.id);
  for (let i = 0; i < entries.length; i++) {
    const ship = entries[i]!;
    const offset = offsets[i]!;
    ship.moveOrder = {
      targetX: sourceGate.sx + offset.dx,
      targetY: sourceGate.sy + offset.dy,
    };
    ship.pendingWarpGateLinkId = linkId;
    // Clear mutually exclusive orders — same exclusion pattern as
    // applyMoveAndJumpShips.
    ship.pendingJumpSystemId = null;
    ship.pendingJumpArrivalAngle = null;
    ship.pendingColoniseTargetId = null;
    ship.constructionOrder = null;
    ship.bombardOrder = null;
  }
}

/**
 * v3M18 — sever every link that has `shipId` as one of its endpoints.
 * Called from `despawnShip` so a destroyed gate doesn't leave orphan
 * links pointing into thin air. Idempotent + cheap; the link Map is
 * small in practice (< 10 typical, capped at one per gate).
 */
export function severWarpGateLinksForShip(
  state: GameState,
  shipId: ShipInstanceId,
): void {
  // Collect-then-delete to avoid mutating during iteration. We rarely
  // sever more than 1 entry (each gate is at most one link's endpoint),
  // so the temporary array is a 0–1 element list in the common case.
  const dead: WarpGateLinkId[] = [];
  for (const link of state.warpGateLinks.values()) {
    if (link.gateAId === shipId || link.gateBId === shipId) dead.push(link.id);
  }
  for (const id of dead) state.warpGateLinks.delete(id);
}

// ---------------------------------------------------------------------------
// v3M17.2 — Carrier squadron commands
// ---------------------------------------------------------------------------

/**
 * v3M17.5 — Pre-jump auto-recall. Walks the batch of jumping ships and,
 * for each that is a carrier (`embarkedSquadronCount !== undefined`)
 * with at least one own deployed squadron, recalls them all before the
 * jump fires. Squadrons cannot enter hyperspace per the v3M17.0 design
 * lock, so this is the price of entering one — the squadron despawns
 * and its surviving fighters credit to the carrier's rebuild
 * accumulator (per `recallAllOwnSquadrons` semantics).
 *
 * Shared between all three jump appliers (`applyJumpShips`,
 * `applyMoveAndJumpShips`, `applyJumpShipsViaWarpGate`). Idempotent on
 * non-carrier batches and on carriers with no deployed squadrons.
 */
function preJumpRecallCarrierSquadrons(
  state: GameState,
  ships: readonly ShipInstance[],
): void {
  for (const ship of ships) {
    if (ship.embarkedSquadronCount === undefined) continue; // not a carrier
    recallAllOwnSquadrons(state, ship);
  }
}

/**
 * Manual launch override. Validates carrier ownership + carrier-template
 * + non-disabled / non-cloaked / not mid-jump / not destroyed; on
 * success spawns all docked squadrons via `launchAllDocked`. Bypasses
 * the Attack Behaviour gate (the manual button is the player's
 * explicit override). Suppresses if any of the carrier's own squadrons
 * are already deployed (re-launch waits for the existing wave —
 * matches the auto-launch invariant).
 */
function applyLaunchCarrierSquadrons(
  state: GameState,
  empireId: EmpireId,
  carrierShipId: ShipInstanceId,
): void {
  const empire = state.empires.get(empireId);
  if (!empire) return;
  const carrier = state.ships.get(carrierShipId);
  if (!carrier) return;
  if (carrier.ownerEmpireId !== empireId) return;
  if (carrier.systemId === null) return; // mid-jump
  if (carrier.disabledTicks > 0) return; // EMP
  if (carrier.cloaked) return;
  if (carrier.currentHull <= 0) return;
  if ((carrier.embarkedSquadronCount ?? 0) === 0) return;
  const tmpl = state.shipTemplates.get(`${empire.raceId}:${carrier.chassis}`);
  if (!tmpl) return;
  if (!isCarrierTemplate(tmpl)) return;
  // Re-launch suppressed while any own squadron deployed.
  for (const ship of state.ships.values()) {
    if (ship.parentCarrierShipId === carrier.id) return;
  }
  launchAllDocked(state, carrier, empire);
}

/**
 * Manual recall override. Validates carrier ownership + carrier-template;
 * recalls every squadron whose `parentCarrierShipId === carrier.id`.
 * Bypasses the Defense Behaviour gate. Mid-jump / destroyed carriers
 * are no-ops (the sever / pre-jump auto-recall paths handle those).
 */
function applyRecallCarrierSquadrons(
  state: GameState,
  empireId: EmpireId,
  carrierShipId: ShipInstanceId,
): void {
  const empire = state.empires.get(empireId);
  if (!empire) return;
  const carrier = state.ships.get(carrierShipId);
  if (!carrier) return;
  if (carrier.ownerEmpireId !== empireId) return;
  if (carrier.currentHull <= 0) return;
  const tmpl = state.shipTemplates.get(`${empire.raceId}:${carrier.chassis}`);
  if (!tmpl) return;
  if (!isCarrierTemplate(tmpl)) return;
  recallAllOwnSquadrons(state, carrier);
}

/**
 * Apply a per-carrier Attack Behaviour change. Validates ownership +
 * carrier-template. Setting on a non-carrier silently no-ops; setting
 * on a destroyed carrier is allowed (so the player can pre-configure
 * a queued carrier that hasn't built yet — though in practice that
 * happens on spawn from the build queue).
 */
function applySetCarrierAttackBehaviour(
  state: GameState,
  empireId: EmpireId,
  carrierShipId: ShipInstanceId,
  behaviour: 'none' | 'visual-range' | 'scan-range',
): void {
  const empire = state.empires.get(empireId);
  if (!empire) return;
  const carrier = state.ships.get(carrierShipId);
  if (!carrier) return;
  if (carrier.ownerEmpireId !== empireId) return;
  const tmpl = state.shipTemplates.get(`${empire.raceId}:${carrier.chassis}`);
  if (!tmpl) return;
  if (!isCarrierTemplate(tmpl)) return;
  carrier.carrierAttackBehaviour = behaviour;
}

/**
 * Apply a per-carrier Defense Behaviour change. Same validation as
 * `applySetCarrierAttackBehaviour`.
 */
function applySetCarrierDefenseBehaviour(
  state: GameState,
  empireId: EmpireId,
  carrierShipId: ShipInstanceId,
  behaviour: 'return-at-low' | 'no-surrender',
): void {
  const empire = state.empires.get(empireId);
  if (!empire) return;
  const carrier = state.ships.get(carrierShipId);
  if (!carrier) return;
  if (carrier.ownerEmpireId !== empireId) return;
  const tmpl = state.shipTemplates.get(`${empire.raceId}:${carrier.chassis}`);
  if (!tmpl) return;
  if (!isCarrierTemplate(tmpl)) return;
  carrier.carrierDefenseBehaviour = behaviour;
}

// ---------------------------------------------------------------------------
// v3M22 — Player ship commands (Patrol / Guard / Explore / waypoints)
// ---------------------------------------------------------------------------

/**
 * v3M22 — set the ship's `commandKind` to `'patrol'`. Loops the ship's
 * existing waypoint chain via the `advance-ship-commands` per-tick
 * pass. Validates ownership; a 0-waypoint patrol is permitted (the
 * pass no-ops until the player adds at least one waypoint).
 */
function applySetShipPatrol(
  state: GameState,
  empireId: EmpireId,
  shipId: ShipInstanceId,
): void {
  const ship = state.ships.get(shipId);
  if (!ship) return;
  if (ship.ownerEmpireId !== empireId) return;
  if (ship.systemId === null) return; // mid-jump
  ship.commandKind = 'patrol';
  // Clear any guard target left over from a previous Guard command.
  ship.guardTargetKind = null;
  ship.guardTargetId = null;
  // Drop the pending single-shot moveOrder so the patrol pass can
  // redirect to the first waypoint on the very next tick. Without
  // this the ship would complete its previous move first, then start
  // patrolling — surprising UX.
  ship.moveOrder = null;
}

/**
 * v3M22 — set the ship into Guard mode targeting a planet (orbit) or
 * a friendly ship (follow). Validates ownership + target existence +
 * same-system reachability. The target's friendliness is enforced
 * for ship-guard (`target.ownerEmpireId === empireId`); planet-guard
 * is allowed regardless of planet ownership (player may want to guard
 * a neutral or contested planet).
 */
function applySetShipGuard(
  state: GameState,
  empireId: EmpireId,
  shipId: ShipInstanceId,
  targetKind: 'planet' | 'ship',
  targetId: number,
): void {
  const ship = state.ships.get(shipId);
  if (!ship) return;
  if (ship.ownerEmpireId !== empireId) return;
  if (ship.systemId === null) return;
  if (targetKind === 'planet') {
    const planet = state.planets.get(targetId);
    if (!planet) return;
    if (planet.systemId !== ship.systemId) return; // must share system
  } else {
    const target = state.ships.get(targetId);
    if (!target) return;
    if (target.ownerEmpireId !== empireId) return; // friendly only
    if (target.systemId !== ship.systemId) return;
  }
  ship.commandKind = 'guard';
  ship.guardTargetKind = targetKind;
  ship.guardTargetId = targetId;
  // Patrol-style waypoints don't apply in Guard; clear so the per-tick
  // pass doesn't try to consume them.
  ship.waypoints = [];
  // Drop any prior single-shot moveOrder so the guard pass can write
  // a fresh orbit / leash target on the next tick. (See applySetShipPatrol
  // for the same rationale.)
  ship.moveOrder = null;
}

/**
 * v3M22 — set the ship into Explore mode. The `advance-ship-commands`
 * pass (added in v3M22.3) auto-jumps to the least-scouted reachable
 * system every AI-tick interval until exhausted, then auto-clears to
 * 'idle'. v3M22.2 wires the command but the consuming behaviour lands
 * in v3M22.3 — until then setting Explore is a no-op at the per-tick
 * level. Field is set so the UI can already reflect the intent.
 */
function applySetShipExplore(
  state: GameState,
  empireId: EmpireId,
  shipId: ShipInstanceId,
): void {
  const ship = state.ships.get(shipId);
  if (!ship) return;
  if (ship.ownerEmpireId !== empireId) return;
  ship.commandKind = 'explore';
  ship.guardTargetKind = null;
  ship.guardTargetId = null;
  ship.waypoints = [];
}

/**
 * v3M22 — replace the ship's waypoint queue with `waypoints`. Plain
 * RMB-on-destination UX. Setting `[]` clears the queue without changing
 * `commandKind` (a patrolling ship with cleared waypoints will simply
 * no-op until new waypoints are added or the command is cleared).
 */
function applySetShipWaypoints(
  state: GameState,
  empireId: EmpireId,
  shipId: ShipInstanceId,
  waypoints: ReadonlyArray<{ targetX: number; targetY: number }>,
): void {
  const ship = state.ships.get(shipId);
  if (!ship) return;
  if (ship.ownerEmpireId !== empireId) return;
  ship.waypoints = waypoints.map((w) => ({ targetX: w.targetX, targetY: w.targetY }));
}

/**
 * v3M22 — append one waypoint to the ship's queue. Shift+RMB-on-
 * destination UX.
 */
function applyAppendShipWaypoint(
  state: GameState,
  empireId: EmpireId,
  shipId: ShipInstanceId,
  targetX: number,
  targetY: number,
): void {
  const ship = state.ships.get(shipId);
  if (!ship) return;
  if (ship.ownerEmpireId !== empireId) return;
  ship.waypoints.push({ targetX, targetY });
  // MFix10.4 — auto-promote idle ships to 'patrol' so the queue
  // actually drives movement. Without this, Shift+RMB on an idle ship
  // appends to `waypoints` but `advanceShipCommands` never consumes
  // them (the patrol pass keys on `commandKind === 'patrol'`). The
  // existing `moveOrder` is left untouched — patrol pop only fires
  // once `moveOrder === null`, so the ship completes its in-flight
  // single-shot move first, then picks up the queue. 'guard' /
  // 'explore' modes are preserved on the assumption the player set
  // them deliberately; the player can still chain waypoints onto
  // those modes (the queue stays put for whenever they switch).
  if (ship.commandKind === 'idle') {
    ship.commandKind = 'patrol';
  }
}

/**
 * v3M22 — clear all v3M22 command state on the ship. Stop button + Esc
 * + cancel-on-jump path. Does not touch the ship's existing single-shot
 * orders (moveOrder, bombardOrder, etc.) — those retain their separate
 * lifecycle.
 */
function applyClearShipCommand(
  state: GameState,
  empireId: EmpireId,
  shipId: ShipInstanceId,
): void {
  const ship = state.ships.get(shipId);
  if (!ship) return;
  if (ship.ownerEmpireId !== empireId) return;
  clearShipCommandFields(ship);
}

/**
 * v3M22 — unconditional clear of every v3M22 field on the ship.
 * Used by the player Stop path (Stop button / Esc / explicit
 * `clearShipCommand` Command). Exported for use by tests and
 * specialised paths that need a hard reset.
 */
export function clearShipCommandFields(ship: ShipInstance): void {
  ship.commandKind = 'idle';
  ship.waypoints = [];
  ship.guardTargetKind = null;
  ship.guardTargetId = null;
}

/**
 * v3M22 — soft cancel used by the jump-entry sites (`applyJumpShips`,
 * `tryStartPendingJump`, `tryStartPendingWarpGateJump`). Always clears
 * `waypoints` + guard target fields per the v3M22.0 cancel-on-jump
 * rule, but PRESERVES `commandKind === 'explore'` because Explore is
 * a multi-jump command by design — it auto-jumps to unscouted
 * systems repeatedly until exhausted, and self-cancellation on its
 * own jumps would defeat the purpose. All other commandKinds revert
 * to `'idle'`.
 *
 * The original v3M22.0 design lock language ("any v3M22 command
 * cancels on jump") was overbroad — Explore is the explicit
 * exception. Player-initiated jumps that interrupt an Explore (e.g.
 * the player manually orders the explorer ship to a different system)
 * are handled by the explicit `clearShipCommand` Command, not by this
 * jump-entry helper.
 */
export function cancelShipCommandOnJump(ship: ShipInstance): void {
  ship.waypoints = [];
  ship.guardTargetKind = null;
  ship.guardTargetId = null;
  if (ship.commandKind !== 'explore') {
    ship.commandKind = 'idle';
  }
}

/**
 * v3M21.2 — set or clear an empire's diplomatic focus target. Validates
 * the target is currently a neutral planet (per `DIPLOMACY.md §4`); per-
 * tick Diplomatic income routing in `advance.ts` reads
 * `empire.diplomaticFocus` to decide whether to fold income into the
 * treasury (focus null) or into the focused planet's per-empire pool
 * (focus non-null).
 *
 * Validation chain (silent no-op on rejection):
 *   1. Empire exists.
 *   2. `planetId === null` clears focus unconditionally — accepted.
 *   3. Otherwise the planet exists.
 *   4. The planet is currently a neutral
 *      (`ownerEmpireId === EMPIRE_NEUTRAL_ID`). Owned planets and
 *      uninhabited planets are rejected.
 *
 * Previously-accumulated Influence on the prior focus target stays put
 * (one-way ratchet — `DIPLOMACY.md §5`).
 */
function applySetDiplomaticFocus(
  state: GameState,
  empireId: EmpireId,
  planetId: PlanetId | null,
): void {
  const empire = state.empires.get(empireId);
  if (!empire) return;
  if (planetId === null) {
    empire.diplomaticFocus = null;
    return;
  }
  const planet = state.planets.get(planetId);
  if (!planet) return;
  if (planet.ownerEmpireId !== EMPIRE_NEUTRAL_ID) return;
  empire.diplomaticFocus = planetId;
}

/**
 * v3M21.2 — one-shot transfer from `empire.resources.diplomatic` into
 * the focus target's per-empire Influence pool. See the Command-union
 * variant comment for the full validation chain. No partial transfer
 * on rejection — atomic.
 */
function applyTransferDiplomaticToFocus(
  state: GameState,
  empireId: EmpireId,
  amount: number,
): void {
  if (amount <= 0) return;
  const empire = state.empires.get(empireId);
  if (!empire) return;
  if (empire.diplomaticFocus === null) return;
  const planet = state.planets.get(empire.diplomaticFocus);
  if (!planet) return;
  if (planet.ownerEmpireId !== EMPIRE_NEUTRAL_ID) return;
  // v3M21.4 — Federation lock: a non-federated empire's pool caps at
  // federationThreshold. Clamp the transferable amount so the call
  // doesn't lossy-debit treasury for Influence that won't fit. If the
  // pool is already at or above the cap, the transfer is a no-op
  // rather than a treasury sink. Treasury insufficiency still rejects
  // the whole transfer (matches the prior atomic shape).
  const prior = planet.influence.get(empireId) ?? 0;
  const cap =
    planet.federatedWith !== null && planet.federatedWith !== empireId
      ? state.balanceData.diplomacy.thresholds.federation
      : Infinity;
  const room = Math.max(0, cap - prior);
  const effectiveAmount = Math.min(amount, room);
  if (effectiveAmount <= 0) return;
  if (empire.resources.diplomatic < effectiveAmount) return;
  empire.resources.diplomatic -= effectiveAmount;
  planet.influence.set(empireId, prior + effectiveAmount);
}

/**
 * v3M21.3 — invoke the Annex outcome on a neutral planet
 * (`DIPLOMACY.md §6.1`). See the Command-union variant comment for
 * the full validation chain.
 *
 * Effect via `flipPlanet`: ownerEmpireId flips to invoking empire;
 * Loyalty resets to 100; pop + generic structures transfer; v3M21
 * diplomatic state on the planet (Influence Map, Federation, Trade,
 * Vassal, every empire's diplomaticFocus pointing here) cleared.
 *
 * Threshold acts as a gate, not a cost — the empire's accumulated
 * Influence is wiped by the side-effect cleanup (the planet's
 * Influence Map is cleared) but the empire's resource pools are
 * unchanged.
 */
function applyInvokeAnnex(
  state: GameState,
  empireId: EmpireId,
  planetId: PlanetId,
): void {
  const empire = state.empires.get(empireId);
  if (!empire) return;
  if (empireId === EMPIRE_NEUTRAL_ID) return; // synthetic neutral can't invoke
  const planet = state.planets.get(planetId);
  if (!planet) return;
  if (planet.ownerEmpireId !== EMPIRE_NEUTRAL_ID) {
    pushRejection(
      state,
      'invokeAnnex',
      empireId,
      `${planet.name} is not a neutral world.`,
    );
    return;
  }
  const annexThreshold = state.balanceData.diplomacy.thresholds.annex;
  const myInfluence = planet.influence.get(empireId) ?? 0;
  if (myInfluence < annexThreshold) {
    pushRejection(
      state,
      'invokeAnnex',
      empireId,
      `Need ${annexThreshold} Influence on ${planet.name} to annex (have ${myInfluence}).`,
    );
    return;
  }
  flipPlanet(state, planet, empireId);
}

/**
 * v3M21.3 — invoke the Trade outcome on a neutral planet
 * (`DIPLOMACY.md §6.3`). See the Command-union variant comment for the
 * full validation chain. Idempotent: re-invoking when already trading
 * is a silent no-op rather than double-pushing onto `tradingWith[]`.
 * The threshold is a gate — Influence is not drained.
 */
function applyInvokeTrade(
  state: GameState,
  empireId: EmpireId,
  planetId: PlanetId,
): void {
  if (empireId === EMPIRE_NEUTRAL_ID) return;
  const empire = state.empires.get(empireId);
  if (!empire) return;
  const planet = state.planets.get(planetId);
  if (!planet) return;
  if (planet.ownerEmpireId !== EMPIRE_NEUTRAL_ID) {
    pushRejection(
      state,
      'invokeTrade',
      empireId,
      `${planet.name} is not a neutral world.`,
    );
    return;
  }
  const tradeThreshold = state.balanceData.diplomacy.thresholds.trade;
  const myInfluence = planet.influence.get(empireId) ?? 0;
  if (myInfluence < tradeThreshold) {
    pushRejection(
      state,
      'invokeTrade',
      empireId,
      `Need ${tradeThreshold} Influence on ${planet.name} to open Trade (have ${myInfluence}).`,
    );
    return;
  }
  if (planet.tradingWith.includes(empireId)) return; // idempotent
  planet.tradingWith.push(empireId);
}

/**
 * v3M21.3 — empire voluntarily ends a Trade arrangement. Silent no-op
 * if the empire is not currently trading with this planet (matches the
 * idempotent shape of the v3M22 ship-command appliers).
 */
function applyClearTrade(
  state: GameState,
  empireId: EmpireId,
  planetId: PlanetId,
): void {
  const empire = state.empires.get(empireId);
  if (!empire) return;
  const planet = state.planets.get(planetId);
  if (!planet) return;
  const idx = planet.tradingWith.indexOf(empireId);
  if (idx < 0) return;
  planet.tradingWith.splice(idx, 1);
}

/**
 * v3M21.4 — invoke the Federation outcome on a neutral planet
 * (`DIPLOMACY.md §6.2`). See the Command-union variant comment for the
 * full validation chain. Effect: set `Planet.federatedWith = empireId`
 * and add the planet's system to the invoker's `scoutedSystemIds`
 * (one-way vision reveal — persists across federation clear).
 *
 * Idempotent — re-invoking when already the federated empire is a
 * silent no-op rather than a spurious re-set.
 */
function applyInvokeFederation(
  state: GameState,
  empireId: EmpireId,
  planetId: PlanetId,
): void {
  if (empireId === EMPIRE_NEUTRAL_ID) return;
  const empire = state.empires.get(empireId);
  if (!empire) return;
  const planet = state.planets.get(planetId);
  if (!planet) return;
  if (planet.ownerEmpireId !== EMPIRE_NEUTRAL_ID) {
    pushRejection(
      state,
      'invokeFederation',
      empireId,
      `${planet.name} is not a neutral world.`,
    );
    return;
  }
  // Reject if already federated to a different empire — the lock
  // semantically prevents this anyway, but the explicit check covers
  // the edge where the lock hasn't ticked through yet.
  if (planet.federatedWith !== null && planet.federatedWith !== empireId) {
    pushRejection(
      state,
      'invokeFederation',
      empireId,
      `${planet.name} is already federated with another empire.`,
    );
    return;
  }
  const federationThreshold = state.balanceData.diplomacy.thresholds.federation;
  const myInfluence = planet.influence.get(empireId) ?? 0;
  if (myInfluence < federationThreshold) {
    pushRejection(
      state,
      'invokeFederation',
      empireId,
      `Need ${federationThreshold} Influence on ${planet.name} to federate (have ${myInfluence}).`,
    );
    return;
  }
  planet.federatedWith = empireId;
  // Vision share — the federated empire gains the planet's system in
  // their FoW reveal set.
  empire.scoutedSystemIds.add(planet.systemId);
}

/**
 * v3M21.4 — federated empire voluntarily ends the federation. Sets
 * `Planet.federatedWith = null`. `scoutedSystemIds` reveal stays
 * (one-way) — matches existing FoW behaviour.
 */
function applyClearFederation(
  state: GameState,
  empireId: EmpireId,
  planetId: PlanetId,
): void {
  const empire = state.empires.get(empireId);
  if (!empire) return;
  const planet = state.planets.get(planetId);
  if (!planet) return;
  if (planet.federatedWith !== empireId) return; // only the holder can clear
  planet.federatedWith = null;
}

/**
 * v3M21.4 — invoke the Vassal outcome on a neutral planet
 * (`DIPLOMACY.md §6.4`). See the Command-union variant comment for
 * the full validation chain. Effect: set `Planet.vassalOf = empireId`,
 * overwriting any prior suzerain (vassal flip). Per-tick tribute
 * routing in `advance.ts:3c` (v3M21.4.4) reads the field and routes
 * `vassalTributeFraction` of the planet's per-tick resource
 * production to the suzerain.
 */
function applyInvokeVassal(
  state: GameState,
  empireId: EmpireId,
  planetId: PlanetId,
): void {
  if (empireId === EMPIRE_NEUTRAL_ID) return;
  const empire = state.empires.get(empireId);
  if (!empire) return;
  const planet = state.planets.get(planetId);
  if (!planet) return;
  if (planet.ownerEmpireId !== EMPIRE_NEUTRAL_ID) {
    pushRejection(
      state,
      'invokeVassal',
      empireId,
      `${planet.name} is not a neutral world.`,
    );
    return;
  }
  // Federation supersedes Vassal — even the federated empire cannot
  // invoke Vassal on their own federated planet (matches §6.4
  // "possible only if planet is not federated").
  if (planet.federatedWith !== null) {
    pushRejection(
      state,
      'invokeVassal',
      empireId,
      `${planet.name} is federated and cannot become a Vassal.`,
    );
    return;
  }
  const vassalThreshold = state.balanceData.diplomacy.thresholds.vassal;
  const myInfluence = planet.influence.get(empireId) ?? 0;
  if (myInfluence < vassalThreshold) {
    pushRejection(
      state,
      'invokeVassal',
      empireId,
      `Need ${vassalThreshold} Influence on ${planet.name} to vassalise (have ${myInfluence}).`,
    );
    return;
  }
  planet.vassalOf = empireId; // overwrite-on-flip
}

/**
 * v3M21.4 — current suzerain voluntarily ends vassalage. Silent no-op
 * if the empire is not the current suzerain.
 */
function applyClearVassal(
  state: GameState,
  empireId: EmpireId,
  planetId: PlanetId,
): void {
  const empire = state.empires.get(empireId);
  if (!empire) return;
  const planet = state.planets.get(planetId);
  if (!planet) return;
  if (planet.vassalOf !== empireId) return;
  planet.vassalOf = null;
}

/** Apply a batch of commands in declaration order. */
export function applyCommands(state: GameState, cmds: readonly Command[]): void {
  // v4M1.3 — clear the transient rejection ledger at the start of every
  // batch. Appliers below push into `state.commandRejections` when a
  // command silently no-ops; the alert-feed reads the buffer on the same
  // tick and surfaces each entry as an amber toast. Buffer is excluded
  // from the save body.
  state.commandRejections = [];
  for (const cmd of cmds) applyCommand(state, cmd);
}

/**
 * v4M1.3 — internal helper that appends a rejection record. Kept tiny so
 * call sites read as `pushRejection(state, 'jumpShips', empireId, 'Not
 * enough fuel')`. `reason` should be a short player-facing one-liner —
 * see the `CommandRejection` doc comment in `state/types.ts`. `empireId`
 * may be undefined when the applier rejected before resolving an empire
 * (e.g. lookup failed); the alert-feed filters out such entries.
 */
function pushRejection(
  state: GameState,
  cmdKind: string,
  empireId: EmpireId | undefined,
  reason: string,
): void {
  state.commandRejections.push({ cmdKind, empireId, reason });
}
